import {
  apiAgentPropertiesAddress,
  apiPropertyItem,
  apiPublicPropertiesList,
} from '@src/v3/interfaces/api/publicProperties';
import { apiAgentPropertiesList } from '@src/v3/interfaces/api/agentProperties';
import {
  Property,
  ImageInformation,
  Address,
  AmenityInformation,
  PlaceInformation,
  PropertyDetails,
  chargeInformation,
  SORT_FILTER_MAP,
  Agent,
} from '@src/v3/interfaces/app/propertyLists';
import { PropertyFilter } from '@src/v3/interfaces/utils/http';
import { format, isPast, isToday, isYesterday } from 'date-fns';
import {
  Amenity,
  FinixAddress,
  RentalHistoryFormObject,
  RenterProfile,
} from '@src/v3/interfaces/profile';
import {
  PhoneDetailsApi,
  RenterApiProfile,
} from '@src/v3/interfaces/api/userProfile';
import {
  CollaboratorInformation,
  Documents,
  DocumentsInformation,
  AddPropertyPayload,
  CreatePropertyPayload,
  ImageInformation as ImageInfoInterface,
  GeneralInformation,
  EconomicInformationStep,
  AmenityInformation as AddPropertyAmenityInformation,
} from '@src/v3/interfaces/app/agent/addProperty';
import { PropertyPreviewDetails } from '@src/v3/interfaces/app/addProperty';
import { EditUtility, FEE_TYPES } from '@src/v3/constants/addProperty';
import { DATE_FORMATS, TIME_FORMATS } from '@src/v3/constants/dateFormat';
import {
  ApplicantType,
  ApplicationState,
} from '@src/v3/interfaces/components/application';
import {
  APPLICATION_CO_APPLICANT_ENUM,
  APPLICATION_STATES_ENUM,
  APPLICATION_STATES_LABELS_ENUM,
} from '@src/v3/constants/application';
import { ZEME_USERS_ROLE } from '@src/v3/constants/configs';
import {
  Applicant,
  GET_SORT_APPLICATION_LABLE_ENUM,
  SORT_OPTIONS,
} from '@src/v3/interfaces/app/renter/my-application';
import { getUserId, getUserType } from './localStorage';
import { InitialEditRenterValues } from '@src/v3/interfaces/app/settings/edit-profile/renterEditProfile';
import { RegisterRenterPayload } from '@src/v3/interfaces/components/wizard/register/details';
import {
  AgentRegisterEditValues,
  AgentRegisterPayload,
  InitialAgentRegisterValues,
} from '@src/v3/interfaces/app/auth/register';
import { capitalize, cloneDeep, lowerCase, round } from 'lodash';
import { AddressAutoSearch } from '@src/v3/interfaces/components/searchAutocomplete';
import { DEFAULT_HEADER_HEIGHT, NAME_PATTERN } from '@src/v3/constants/common';
import { ApplicationCRMFilter } from '@src/v3/interfaces/api/agentCRM';
import {
  OneOffCreditUserData,
  OneOffCreditUserApiData,
} from '@src/v3/interfaces/oneOffCredit';
import { AllApplication } from '@src/v3/interfaces/app/renter/my-application';
import { ApplicationFilters } from '../interfaces/components/crm';
import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';

/**
 *
 * @param {number} amount
 * @param {boolean} convertToShortRepresentation
 * @returns {String}
 */
const formatCurrency = (
  amount: number,
  convertToShortRepresentation = false
) => {
  const formatterOptions: Intl.NumberFormatOptions = {
    style: convertToShortRepresentation ? 'decimal' : 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  if (convertToShortRepresentation) {
    const suffixes = ['', 'K', 'M', 'B', 'T'];

    if (amount < 1000) return `$${amount}`; // Include '$' for amounts less than 1,000

    const logValue = Math.log10(amount);
    const suffixIndex = Math.floor(logValue / 3);
    const shortValue = amount / Math.pow(1000, suffixIndex);

    // Check if the shortValue is an integer (no decimal part) or not
    if (shortValue % 1 === 0) {
      return `$${shortValue.toFixed(0)}${suffixes[suffixIndex]}`;
    } else {
      const formattedValue = shortValue.toFixed(1).replace(/(\.0+|0+)$/, ''); // Remove .0 and trailing zeros if present
      return `$${formattedValue}${suffixes[suffixIndex]}`;
    }
  }

  return new Intl.NumberFormat('en-US', formatterOptions).format(amount);
};

/**
 * Formats a currency amount to two decimal places with rounding.
 *
 * @param {number} amount - The amount to be formatted.
 * @return {string} The formatted currency amount.
 */
const formatCurrencyTwoDigitWithRound = (amount: number) => {
  return `$${(Math.round(amount * 100) / 100).toFixed(2)}`;
};

/**
 * This method is used to get formatted data for map. Map component will accept only specific format.
 * Format mentioned in return state of method.
 * @param {Array} list
 * @returns array
 */
const formatMapComponentData = (list: Property[]) => {
  let mapList = [];

  mapList = list.map((data: Property) => {
    const {
      generalInformation: {
        address: {
          coordinates: { lat = 0, lng = 0 } = {},
          primary = '',
          state = '',
          country = '',
          postalCode = '',
        } = {},
        bathrooms = 0,
        bedrooms = 0,
        squareFootage = 0,
      } = {},
      economicInformation: { cost = 0 } = {},
      imageInformation: { images },
    } = data || {};
    // Store first image to map box component
    const image = images.length > 0 ? images[0].image : '';

    return {
      lat,
      lng,
      isSaved:
        (getUserType() === ZEME_USERS_ROLE.AGENT ||
          getUserType() === ZEME_USERS_ROLE.LANDLORD) &&
        data?.generalInformation?.agent?.id === getUserId(),
      popup: {
        address: `${primary || ''}, ${state || ''}, ${country || ''}, ${
          postalCode || ''
        }`,
        bedrooms,
        bathrooms,
        squareFootage,
        cost,
        image,
      },
    };
  });

  return mapList;
};

/**
 * This method is used to convert API response to FE response which we used in other component.
 * The reason behind creating this helper function is If api missing key then FE will not break.
 *
 * @param {Array} list
 * @returns {Array} Custom response
 */
const transformAllPublicPropertiesData = (list: apiPublicPropertiesList[]) => {
  let propertyList = [];

  propertyList = list.map((item: apiPublicPropertiesList) => {
    return {
      id: item.id || '',
      generalInformation: {
        agent: {
          id: item.general_information?.agent?.id || '',
          generalInformation: {
            firstName:
              item.general_information?.agent?.general_information
                ?.first_name || '',
            middleName:
              item.general_information?.agent?.general_information
                ?.middle_name || '',
            lastName:
              item.general_information?.agent?.general_information?.last_name ||
              '',
            verified:
              item.general_information?.agent?.general_information?.verified ||
              false,
          },
          agentInformation: {
            profilePicture:
              item.general_information?.agent?.agent_information
                ?.profile_picture || '',
            companyName:
              item.general_information?.agent?.agent_information
                ?.company_name || '',
            bio: item.general_information?.agent?.agent_information?.bio || '',
            profileUrlIdentifier:
              item.general_information?.agent?.agent_information
                ?.profile_url_identifier || '',
          },
        },

        address: {
          coordinates: {
            lat: item.general_information?.address?.coordinate?.latitude || 0,
            lng: item.general_information?.address?.coordinate?.longitude || 0,
          },
          primary: item.general_information?.address?.primary || '',
          neighborhood: item.general_information?.address?.neighborhood || '',
          state: item.general_information?.address?.state || '',
          country: item.general_information?.address?.country || '',
          postalCode: item.general_information?.address?.postal_code || '',
          unitCode: item.general_information?.address?.unit_code || null,
          route: item.general_information?.address?.route || '',
        },
        bedrooms: item.general_information?.bedrooms || 0,
        cost: item.economic_information?.cost || 0,
        bathrooms: item.general_information?.bathrooms || 0,
        squareFootage: item.general_information?.square_footage || 0,
        propertyName: item.general_information?.property_name || '',
        dateAvailable: item.general_information?.date_available || '',
        description: item.general_information?.description || '',
        timePosted: item.general_information?.time_posted || '',
        isEasyApply: item.general_information?.is_easy_apply || false,
        isExclusive: item.general_information?.is_exclusive || false,
        nextPaymentDate: item?.general_information?.next_payment_date || '',
      },
      imageInformation: {
        images:
          item.image_information?.images?.map((image) => ({
            id: image.id || 0,
            image: image.image || '',
            thumbnail: image.thumbnail || image.image || '',
          })) || [],
      } as ImageInformation,
      economicInformation: {
        cost: item.economic_information?.cost || 0,
        updatedCost: item.economic_information?.updated_cost || 0,
        minCredit: item.economic_information?.min_credit || 0,
        minSalary: item.economic_information?.min_salary || 0,
        netEffectiveRent: item.economic_information?.net_effective_rent || 0,
        leaseConcession: item.economic_information?.lease_concession || false,
        leaseMonthsFree: item.economic_information?.lease_months_free || 0,
        leaseTerm: item.economic_information?.lease_term || '',
      },
      chargeInformation: {
        brokerFee: item.charge_information?.broker_fee || 0,
      },
      isSave: false,
      amountToBeCharged: item?.amount_to_be_charged || 0,
      creationDate: item?.creation_date || '',
      lastUpdate: item?.last_update || '',
    };
  });

  return propertyList;
};

// Union type for property items from different APIs (agent properties and collaborations)
type AgentPropertyListItem = apiAgentPropertiesList | apiPropertyItem;

const transformAllAgentPropertiesData = (list: AgentPropertyListItem[]) => {
  let propertyList = [];

  propertyList = list.map((item: AgentPropertyListItem) => {
    return {
      id: item.id || '',
      generalInformation: {
        agent: {
          id: item.general_information?.agent?.id || '',
          generalInformation: {
            firstName:
              item.general_information?.agent?.general_information
                ?.first_name || '',
            middleName:
              item.general_information?.agent?.general_information
                ?.middle_name || '',
            lastName:
              item.general_information?.agent?.general_information?.last_name ||
              '',
            verified:
              item.general_information?.agent?.general_information?.verified ||
              false,
          },
          agentInformation: {
            profilePicture:
              item.general_information?.agent?.agent_information
                ?.profile_picture || '',
            companyName:
              item.general_information?.agent?.agent_information
                ?.company_name || '',
            bio: item.general_information?.agent?.agent_information?.bio || '',
            profileUrlIdentifier:
              item?.general_information?.agent?.agent_information
                ?.profile_url_identifier || '',
          },
        },

        address: {
          coordinates: {
            lat: item.general_information?.address?.coordinate?.latitude || 0,
            lng: item.general_information?.address?.coordinate?.longitude || 0,
          },
          primary: item.general_information?.address?.primary || '',
          neighborhood: item.general_information?.address?.neighborhood || '',
          state: item.general_information?.address?.state || '',
          country: item.general_information?.address?.country || '',
          postalCode: item.general_information?.address?.postal_code || '',
          unitCode: item.general_information?.address?.unit_code || null,
          route: item.general_information?.address?.route || '',
        },
        bedrooms: item.general_information?.bedrooms || 0,
        cost: item.economic_information?.cost || 0,
        bathrooms: item.general_information?.bathrooms || 0,
        squareFootage: item.general_information?.square_footage || 0,
        propertyName: item.general_information?.property_name || '',
        dateAvailable: item.general_information?.date_available || '',
        description: item.general_information?.description || '',
        timePosted: item.general_information?.time_posted || '',
        isOpen: item.general_information?.is_open || false,
        sevenDayExpiryDate:
          item.general_information?.seven_day_expiry_date || '',
        sevenDayNonPaymentCycleStartDate:
          item.general_information?.seven_day_non_payment_cycle_start_date ||
          '',
        nextPaymentDate: item.general_information?.next_payment_date || '',
        isFreeThroughSubscription:
          item.general_information?.is_free_through_subscription || false,
        isExclusive: item.general_information?.is_exclusive || false,
      },
      imageInformation: {
        images:
          item.image_information?.images?.map((image) => ({
            id: image.id || 0,
            image: image.thumbnail || '',
            thumbnail: image.thumbnail || image.image || '',
          })) || [],
      } as ImageInformation,
      economicInformation: {
        cost: item.economic_information?.cost || 0,
        updatedCost: item.economic_information?.updated_cost || 0,
        minCredit: item.economic_information?.min_credit || 0,
        minSalary: item.economic_information?.min_salary || 0,
        netEffectiveRent: item.economic_information?.net_effective_rent || 0,
        leaseConcession: item.economic_information?.lease_concession || false,
        leaseMonthsFree: item.economic_information?.lease_months_free || '',
        leaseTerm: item.economic_information?.lease_term || '',
      },
      chargeInformation: {
        brokerFee: item.charge_information?.broker_fee || 0,
      },
      statisticInformation: {
        views: item.statistic_information?.views || 0,
        saves: item.statistic_information?.saves || 0,
        applications: item.statistic_information?.applications || 0,
        submittedApplications:
          item.statistic_information?.submitted_applications || 0,
      },
    };
  });

  return propertyList;
};

/**
 * This method is used to convert API response to FE response which we used in other component.
 * The reason behind creating this helper function is If api missing key then FE will not break.
 *
 * @param {Array} list
 * @returns {Array} Custom response
 */
const transformPropertyItem = (item: apiPropertyItem | undefined | null) => {
  return {
    id: item?.id,
    generalInformation: {
      agent: {
        id: item?.general_information?.agent?.id || 0,
        generalInformation: {
          firstName:
            item?.general_information?.agent?.general_information?.first_name ||
            '',
          middleName:
            item?.general_information?.agent?.general_information
              ?.middle_name || '',
          lastName:
            item?.general_information?.agent?.general_information?.last_name ||
            '',
          verified:
            item?.general_information?.agent?.general_information?.verified ||
            false,
        },
        agentInformation: {
          profilePicture:
            item?.general_information?.agent?.agent_information
              ?.profile_picture || '',
          companyName:
            item?.general_information?.agent?.agent_information?.company_name ||
            '',
          bio: item?.general_information?.agent?.agent_information?.bio || '',
          profileUrlIdentifier:
            item?.general_information?.agent?.agent_information
              ?.profile_url_identifier || '',
        },
      },
      collaboratorsList:
        item?.collaborators_list?.map((collaborator) => ({
          id: collaborator.id || '',
          email: collaborator.email || '',
          role: collaborator.role || '',
          status: collaborator.status || '',
          invitedAt: collaborator.invited_at || '',
          respondedAt: collaborator.responded_at || null,
          agentInfo: {
            id: collaborator.agent_info?.id || '',
            generalInformation: {
              firstName:
                collaborator.agent_info?.general_information?.first_name || '',
              middleName:
                collaborator.agent_info?.general_information?.middle_name || '',
              lastName:
                collaborator.agent_info?.general_information?.last_name || '',
              verified: false,
            },
            agentInformation: {
              profilePicture:
                collaborator.agent_info?.agent_information?.profile_picture ||
                '',
              companyName:
                collaborator.agent_info?.agent_information?.company_name || '',
              bio: collaborator.agent_info?.agent_information?.bio || '',
              profileUrlIdentifier:
                collaborator.agent_info?.agent_information
                  ?.profile_url_identifier || '',
            },
          },
        })) || [],
      address: {
        coordinates: {
          lat: item?.general_information?.address?.coordinate?.latitude || 0,
          lng: item?.general_information?.address?.coordinate?.longitude || 0,
        },
        primary: item?.general_information?.address?.primary || '',
        neighborhood: item?.general_information?.address?.neighborhood || '',
        state: item?.general_information?.address?.state || '',
        country: item?.general_information?.address?.country || '',
        postalCode: item?.general_information?.address?.postal_code || '',
        unitCode: item?.general_information?.address?.unit_code || null,
        route: item?.general_information?.address?.route || '',
      } as Address,
      bedrooms: item?.general_information?.bedrooms || 0,
      cost: item?.economic_information?.cost || 0,
      bathrooms: item?.general_information?.bathrooms || 0,
      squareFootage: item?.general_information?.square_footage || 0,
      propertyName: item?.general_information?.property_name || '',
      dateAvailable: item?.general_information?.date_available || '',
      description: item?.general_information?.description || '',
      timePosted: item?.general_information?.time_posted || '',
      isEasyApply: item?.general_information?.is_easy_apply || false,
      isOpen: item?.general_information?.is_open || false,
      isExclusive: item?.general_information?.is_exclusive,
      isApplicationRequestEnabled:
        item?.general_information?.is_application_request_enabled,
    },
    economicInformation: {
      cost: item?.economic_information?.cost || 0,
      updatedCost: item?.economic_information?.updated_cost || 0,
      minCredit: item?.economic_information?.min_credit || 0,
      minSalary: item?.economic_information?.min_salary || 0,
      netEffectiveRent: item?.economic_information?.net_effective_rent || 0,
      leaseMonthsFree: item?.economic_information?.lease_months_free || '',
      leaseTerm: item?.economic_information?.lease_term || '',
      leaseConcession: item?.economic_information?.lease_concession || false,
    },
    imageInformation: {
      images:
        item?.image_information?.images?.map((image) => ({
          id: image.id || 0,
          image: image.image || '',
          thumbnail: image.thumbnail || '',
        })) || [],
    } as ImageInformation,
    amenityInformation: {
      amenities:
        item?.amenity_information?.amenities?.map((amenity) => ({
          type: amenity.type || '',
          included: amenity.included || false,
        })) || [],
    } as AmenityInformation,
    chargeInformation: {
      firstMonthRent: item?.charge_information?.first_month_rent || 0,
      securityDeposit: item?.charge_information?.security_deposit || 0,
      brokerFee: item?.charge_information?.broker_fee || 0,
      applicationFee: item?.charge_information?.application_fee || 0,
      extraFee: item?.charge_information?.extra_fee || [],
    },
    utilityInformation: {
      gas: item?.utility_information?.gas || false,
      sewage: item?.utility_information?.sewage || false,
      electricity: item?.utility_information?.electricity || false,
      internet: item?.utility_information?.internet || false,
      water: item?.utility_information?.water || false,
      garbage: item?.utility_information?.garbage || false,
      cable: item?.utility_information?.cable || false,
      satelliteTv: item?.utility_information?.satellite_tv || false,
      otherUtilities: item?.utility_information?.other_utilities || null,
    },
    statisticInformation: {
      views: item?.statistic_information?.views || 0,
      saves: item?.statistic_information?.saves || 0,
      applications: item?.statistic_information?.applications || 0,
    },
    documentInformation: {
      documents: item?.document_information?.documents || [],
    } as DocumentsInformation,
    placeInformation: {
      places:
        item?.related_place_information?.places?.map((place) => ({
          id: place.id,
          image: place.image,
          name: place.name,
          address: place.address,
          url: place.url,
        })) || [],
    } as PlaceInformation,
    creationDate: item?.creation_date || '',
    lastUpdate: item?.last_update || '',
    isSaved: false,
  } as PropertyDetails;
};

/**
 * Agent override for "post on behalf" feature
 */
export interface AgentOverride {
  agentId: string;
  firstName?: string;
  lastName?: string;
  profilePicture?: string | null;
  companyName?: string;
  bio?: string;
  profileUrlIdentifier?: string;
}

/**
 * Transform CreatePropertyPayload to PropertyDetails format for preview
 * @param payload - The CreatePropertyPayload data
 * @param profile - Optional user profile for agent information
 * @param agentOverride - Optional agent override for "post on behalf" feature
 * @returns PropertyDetails formatted object
 */
const transformPayloadToPropertyDetails = (
  payload: CreatePropertyPayload,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  profile?: any,
  agentOverride?: AgentOverride
): PropertyDetails => {
  // Get user profile for agent information
  const currentProfile = profile;
  const currentUserId = getUserId() || '';

  // Transform address to match expected format
  const addressData = payload?.general_information?.address || {};
  const transformedAddress: Address = {
    coordinates: addressData.coordinates || { lat: 0, lng: 0 },
    primary: addressData.primary || addressData.address || '',
    neighborhood: addressData.neighborhood || '',
    state: addressData.state || '',
    country: addressData.country || '',
    postalCode: addressData.postalCode || addressData.postal_code || '',
    unitCode: addressData.unitCode || addressData.unit_code || null,
    route: addressData.route || '',
  };

  // Create agent object - use override if provided (for "post on behalf" feature)
  const agentInfo: Agent = agentOverride
    ? {
        id: agentOverride.agentId,
        generalInformation: {
          firstName: agentOverride.firstName || '',
          middleName: '',
          lastName: agentOverride.lastName || '',
          verified: false,
        },
        agentInformation: {
          profilePicture: agentOverride.profilePicture || '',
          companyName: agentOverride.companyName || '',
          bio: agentOverride.bio || '',
          profileUrlIdentifier: agentOverride.profileUrlIdentifier || '',
        },
      }
    : {
        id: currentUserId,
        generalInformation: {
          firstName: currentProfile?.generalInformation?.firstName || '',
          middleName: currentProfile?.generalInformation?.middleName || '',
          lastName: currentProfile?.generalInformation?.lastName || '',
          verified: false,
        },
        agentInformation: {
          profilePicture:
            currentProfile?.agentInformation?.profilePicture || '',
          companyName: currentProfile?.agentInformation?.companyName || '',
          bio: currentProfile?.agentInformation?.bio || '',
          profileUrlIdentifier:
            currentProfile?.agentInformation?.profileUrlIdentifier || '',
        },
      };

  return {
    id: 'preview',
    generalInformation: {
      propertyName: payload?.general_information?.property_name || '',
      cost: Number(payload?.economic_information?.cost) || 0,
      bedrooms: Number(payload?.general_information?.bedrooms) || 0,
      bathrooms: Number(payload?.general_information?.bathrooms) || 0,
      squareFootage: Number(payload?.general_information?.square_footage) || 0,
      dateAvailable: payload?.general_information?.date_available || '',
      timePosted:
        payload?.general_information?.time_posted || new Date().toISOString(),
      isExclusive: payload?.general_information?.is_exclusive || false,
      isApplicationRequestEnabled:
        payload?.general_information?.is_application_request_enabled || false,
      isEasyApply: payload?.general_information?.is_easy_apply || false,
      description: payload?.general_information?.description || '',
      isOpen: true,
      address: transformedAddress,
      agent: agentInfo,
    },
    economicInformation: {
      cost: Number(payload?.economic_information?.cost) || 0,
      updatedCost: Number(payload?.economic_information?.updated_cost) || 0,
      minCredit: 0,
      minSalary: 0,
      leaseConcession: payload?.economic_information?.lease_concession || false,
      leaseMonthsFree: payload?.economic_information?.lease_months_free || '',
      leaseTerm: payload?.economic_information?.lease_term || '',
      netEffectiveRent:
        Number(payload?.economic_information?.net_effective_rent) || 0,
    },
    chargeInformation: {
      securityDeposit:
        Number(payload?.charge_information?.security_deposit) || 0,
      brokerFee: Number(payload?.charge_information?.broker_fee) || 0,
      applicationFee: Number(payload?.charge_information?.application_fee) || 0,
      extraFee: payload?.charge_information?.extra_fee || [],
    },
    amenityInformation: {
      amenities: payload?.amenity_information?.amenities || [],
    },
    documentInformation: payload?.document_information || { documents: [] },
    imageInformation: {
      images: (
        payload?.image_information?.images ||
        payload?.all_images ||
        []
      ).map((img, index) => {
        // Handle File objects and convert to blob URLs
        let imageUrl = '';
        let thumbnailUrl = '';

        if (img?.image instanceof File) {
          imageUrl = transformFileToBlobURL(img.image);
          thumbnailUrl = imageUrl;
        } else if (typeof img?.image === 'string') {
          imageUrl = img.image;
          thumbnailUrl = img?.thumbnail || img.image;
        } else if (typeof img === 'string') {
          // Handle case where img itself might be a string
          imageUrl = img;
          thumbnailUrl = img;
        }

        return {
          id: String(img?.id || `preview-${index}`),
          image: imageUrl,
          thumbnail: thumbnailUrl,
        };
      }),
    },
    placeInformation: {
      places: [],
    },
    statisticInformation: {
      applications: 0,
      saves: 0,
      views: 0,
      submittedApplications: 0,
    },
    isSaved: false,
    creationDate: new Date().toISOString(),
  };
};

/**
 * This method is used to convert API response to FE response which we used in other component.
 * The reason behind creating this helper function is If api response and add property context state has different structure.
 *
 * @param {Array} list
 * @returns {Array} Custom response
 */
const transFromPropertyToAddPropertyConext = (
  item: apiPropertyItem | undefined | null
) => {
  let netEffectiveFee = 0;
  if (item?.economic_information?.lease_concession) {
    netEffectiveFee = round(
      (item?.economic_information?.cost *
        (Number(item?.economic_information?.lease_term) -
          Number(item?.economic_information?.lease_months_free))) /
        Number(item?.economic_information?.lease_term)
    );
  }

  return {
    general_information: {
      is_open: item?.general_information?.is_open || false,
      agent_id: item?.general_information?.agent
        ? {
            id: item?.general_information?.agent?.id,
            generalInformation: {
              firstName:
                item?.general_information?.agent?.general_information
                  ?.first_name,
              middleName:
                item?.general_information?.agent?.general_information
                  ?.middle_name,
              lastName:
                item?.general_information?.agent?.general_information
                  ?.last_name,
              email:
                item?.general_information?.agent?.general_information?.email,
              verified:
                item?.general_information?.agent?.general_information?.verified,
            },
            agentInformation: {
              profilePicture:
                item?.general_information?.agent?.agent_information
                  ?.profile_picture,
              companyName:
                item?.general_information?.agent?.agent_information
                  ?.company_name,
              bio: item?.general_information?.agent?.agent_information?.bio,
            },
          }
        : '',
      address: item?.general_information?.address.hasOwnProperty('coordinate')
        ? ({
            coordinates: {
              lat: item?.general_information?.address?.coordinate.latitude,
              lng: item?.general_information?.address?.coordinate.longitude,
            },
            primary: item?.general_information?.address?.primary,
            state: item?.general_information?.address?.state,
            neighborhood: item?.general_information?.address?.neighborhood,
            country: item?.general_information?.address?.country,
            postalCode: item?.general_information?.address?.postal_code,
            unitCode: item?.general_information?.address?.unit_code,
            route: item?.general_information?.address?.route,
            place_id: item?.general_information?.address?.place_id || '',
          } as Address)
        : {
            address: item?.general_information?.address
              ? combineAddress(item?.general_information?.address)
              : '',
            place_id:
              item?.general_information?.address?.primary ||
              item?.general_information?.address?.unit_code ||
              '',
          },
      property_name: item?.general_information?.property_name || '',
      apartment_number: 0,
      is_easy_apply: item?.general_information?.is_easy_apply || false,
      description: item?.general_information?.description || '',
      unit: item?.general_information?.address?.unit_code
        ? item?.general_information?.address?.unit_code + ''
        : '',
      floor: item?.general_information?.address?.floor
        ? item?.general_information?.address?.floor + ''
        : '',
      bedrooms:
        item?.general_information?.bedrooms !== undefined &&
        item?.general_information?.bedrooms !== null
          ? item?.general_information?.bedrooms + ''
          : '',

      square_footage: item?.general_information?.square_footage
        ? item?.general_information?.square_footage + ''
        : '',
      date_available: item?.general_information?.date_available || '',
      bedroom:
        item?.general_information?.bedrooms &&
        item?.general_information?.bedrooms < 5
          ? item?.general_information?.bedrooms + ''
          : '',
      bedroom_other:
        item?.general_information?.bedrooms &&
        item?.general_information?.bedrooms > 5
          ? item?.general_information?.bedrooms
          : '',
      bathrooms:
        item?.general_information?.bathrooms !== undefined &&
        item?.general_information?.bathrooms !== null
          ? item?.general_information?.bathrooms + ''
          : '',
      bathrooms_other:
        item?.general_information?.bathrooms &&
        item?.general_information?.bathrooms > 3.5
          ? item?.general_information?.bathrooms
          : '',
      time_posted: item?.general_information.time_posted || '',
      is_exclusive: item?.general_information?.is_exclusive,
      is_application_request_enabled:
        item?.general_information?.is_application_request_enabled,
    },
    economic_information: {
      lease_concession: Boolean(item?.economic_information?.lease_concession),
      lease_months_free: item?.economic_information?.lease_months_free
        ? item?.economic_information?.lease_months_free + ''
        : '',
      lease_term: item?.economic_information?.lease_term
        ? item?.economic_information?.lease_term + ''
        : '',
      net_effective_rent: netEffectiveFee || '',
      cost: item?.economic_information?.cost
        ? item?.economic_information?.cost + ''
        : '',
      updatedCost: item?.economic_information?.updated_cost
        ? item?.economic_information?.updated_cost + ''
        : 0,
      minCredit: item?.economic_information?.min_credit
        ? item?.economic_information?.min_credit + ''
        : '',
      minSalary: item?.economic_information?.min_salary
        ? item?.economic_information?.min_salary + ''
        : '',
      first_month_rent: item?.charge_information?.first_month_rent
        ? item?.charge_information?.first_month_rent + ''
        : '',
      security_deposite_amt: item?.charge_information?.security_deposit
        ? item?.charge_information?.security_deposit + ''
        : '',
      broker_fee:
        item?.charge_information?.broker_fee != null &&
        item?.charge_information?.broker_fee > -1
          ? item?.charge_information?.broker_fee + ''
          : '',
      application_fee:
        item?.charge_information?.application_fee != null &&
        item?.charge_information?.application_fee > -1
          ? item?.charge_information?.application_fee + ''
          : '',
      extra_fee:
        item?.charge_information?.extra_fee &&
        item?.charge_information?.extra_fee?.length > 0
          ? 'Yes'
          : 'No',
      extra_fee_fields: item?.charge_information?.extra_fee || [],
      brokerSign: '$',
      utilities: item?.utility_information
        ? Object.keys(item?.utility_information)
            .filter(
              (key) =>
                item?.utility_information.hasOwnProperty(key) &&
                item?.utility_information[
                  key as keyof typeof item.utility_information
                ]
            )
            .map((key) => EditUtility[key as keyof typeof EditUtility])
        : [],
      extra_utilities: item?.utility_information?.other_utilities || '',
    },
    image_information: {
      images:
        item?.image_information?.images?.map((image) => ({
          image: image.image || '',
        })) || [],
    } as ImageInfoInterface,
    images: {
      images:
        item?.image_information?.images?.map((image) => ({
          image: image.image || '',
          id: image.id || '',
        })) || [],
    } as ImageInfoInterface,
    amenity_information: {
      amenities:
        item?.amenity_information?.amenities?.map((amenity) => ({
          type: amenity.type || '',
          included: amenity.included || false,
        })) || [],
    } as AmenityInformation,
    charge_information: {
      first_month_rent: item?.charge_information?.first_month_rent || 0,
      security_deposit: item?.charge_information?.security_deposit || 0,
      broker_fee: item?.charge_information?.broker_fee || 0,
      application_fee: item?.charge_information?.application_fee || 0,
      extra_fee: item?.charge_information?.extra_fee || [],
    },
    utility_information: {
      gas: item?.utility_information?.gas || false,
      sewage: item?.utility_information?.sewage || false,
      electricity: item?.utility_information?.electricity || false,
      internet: item?.utility_information?.internet || false,
      water: item?.utility_information?.water || false,
      garbage: item?.utility_information?.garbage || false,
      cable: item?.utility_information?.cable || false,
      satellite_tv: item?.utility_information?.satellite_tv || false,
      other_utilities: item?.utility_information?.other_utilities || null,
    },
    statisticInformation: {
      views: item?.statistic_information?.views || 0,
      saves: item?.statistic_information?.saves || 0,
      applications: item?.statistic_information?.applications || 0,
    },
    document_information: {
      documents: item?.document_information?.documents || [],
    } as DocumentsInformation,
    related_place_information: {
      places:
        item?.related_place_information?.places?.map((place) => ({
          id: place.id,
          image: place.image,
          name: place.name,
          address: place.address,
          url: place.url || '',
        })) || [],
    } as PlaceInformation,
    collaborator_information: {
      collaborators:
        item?.collaborators_list?.map((collaborator) => ({
          email: collaborator.email,
          role: collaborator.role,
          found: true,
          firstName: collaborator.agent_info?.general_information?.first_name,
          lastName: collaborator.agent_info?.general_information?.last_name,
          profilePicture:
            collaborator.agent_info?.agent_information?.profile_picture,
          status: collaborator.status,
          id: collaborator.id,
          originalRole: collaborator.role,
        })) || [],
    },
    creationDate: item?.creation_date || '',
    lastUpdate: item?.last_update || '',
  } as Partial<AddPropertyPayload>;
};

const translatePropertyAddPropertyAddress = (
  address: apiAgentPropertiesAddress
) => {
  return address.hasOwnProperty('coordinate')
    ? ({
        coordinates: {
          lat: address?.coordinate?.latitude || 0,
          lng: address?.coordinate?.longitude || 0,
        },
        primary: address?.primary,
        state: address?.state,
        neighborhood: address?.neighborhood,
        country: address?.country,
        postalCode: address?.postal_code,
        unitCode: address?.unit_code,
        route: address?.route,
      } as Address)
    : {
        address: address ? combineAddress(address) : '',
        place_id: address?.primary || address?.unit_code || '',
      };
};

/**
 * This method is used to convert the Property context state to create property api's payload.
 * The reason behind creating this helper function is if Context state structure is not same with property preview dialog then
 * It didn't effect on the property preview dialog box so.
 * @param {Array} list
 * @returns {Array} Custom response
 */
const transformCreatePropertyPayloadItem = (
  item: CreatePropertyPayload | undefined | null
) => {
  return {
    id: item?.id || 0,
    generalInformation: {
      agent: item?.general_information?.agent_id || 0,
      address: {
        address: item?.general_information?.address?.address || '',
        place_id: item?.general_information?.address?.place_id || '',
        address_place_id:
          item?.general_information?.address?.address_place_id || '',
        primary: item?.general_information?.address?.primary || '',
        state: item?.general_information?.address?.state || '',
        neighborhood: item?.general_information?.address?.neighborhood || '',
        country: item?.general_information?.address?.country || '',
        unitCode: item?.general_information?.address?.unit_code || '',
        route: item?.general_information?.address?.route || '',
        postalCode: item?.general_information?.address?.postal_code || '',
      },
      unitCode: item?.general_information?.unit || null,
      bedrooms: Number(item?.general_information?.bedrooms) || 0,
      cost: item?.economic_information?.cost || 0,
      bathrooms: item?.general_information?.bathrooms || 0,
      squareFootage: item?.general_information?.square_footage || 0,
      propertyName: item?.general_information?.property_name || '',
      dateAvailable: item?.general_information?.date_available || '',
      description: item?.general_information?.description || '',
      timePosted: item?.general_information?.time_posted || '',
      isEasyApply: item?.general_information?.is_easy_apply || false,
      isExclusive: item?.general_information?.is_exclusive,
    },
    economicInformation: {
      cost: item?.economic_information?.cost || 0,
      updatedCost: item?.economic_information?.updated_cost || 0,
      minCredit: item?.economic_information?.min_credit || 0,
      minSalary: item?.economic_information?.min_salary || 0,
      leaseConcession: item?.economic_information?.lease_concession || false,
      leaseMonthsFree: item?.economic_information?.lease_months_free || '',
      leaseTerm: item?.economic_information?.lease_term || '',
      netEffectiveRent: item?.economic_information?.net_effective_rent || 0,
    },
    imageInformation: {
      images:
        item?.image_information?.images?.map((image) => ({
          image: transformFileToBlobURL(image.image as File) || '',
          rotation: image.rotation || 0,
        })) || [],
    },
    amenityInformation: {
      amenities:
        item?.amenity_information?.amenities?.map((amenity) => ({
          type: amenity.type || '',
          included: amenity.included || false,
        })) || [],
    } as AmenityInformation,
    chargeInformation: {
      securityDeposit: item?.charge_information?.security_deposit || 0,
      brokerFee: item?.charge_information?.broker_fee || 0,
      applicationFee: item?.charge_information?.application_fee || 0,
      extraFee: item?.charge_information?.extra_fee || null,
    },
    utilityInformation: {
      gas: item?.utility_information?.gas || false,
      sewage: item?.utility_information?.sewage || false,
      electricity: item?.utility_information?.electricity || false,
      internet: item?.utility_information?.internet || false,
      water: item?.utility_information?.water || false,
      garbage: item?.utility_information?.garbage || false,
      cable: item?.utility_information?.cable || false,
      satelliteTv: item?.utility_information?.satellite_tv || false,
      otherUtilities: item?.utility_information?.other_utilities || null,
    },
    statisticInformation: {
      views: item?.statistic_information?.views || 0,
      saves: item?.statistic_information?.saves || 0,
      applications: item?.statistic_information?.applications || 0,
    },
    documentInformation: {
      documents: item?.document_information?.documents || [],
    } as DocumentsInformation,
    placeInformation: {
      places:
        item?.related_place_information?.places?.map((place) => ({
          id: place.id,
          image: place.image,
          name: place.name,
          address: place.address,
          url: place.url || '',
        })) || [],
    } as PlaceInformation,
    creationDate: item?.creation_date || '',
    lastUpdate: item?.last_update || '',
  } as PropertyPreviewDetails;
};

/**
 * Builds E.164 phone number from national number + dial code (e.g. for API payloads).
 * Uses parsePhoneNumberFromString with defaultCallingCode (digits only).
 */
export const buildE164PhoneNumber = (
  phone: string | undefined,
  countryCode: string | undefined
): string => {
  const defaultCallingCode = (countryCode || '').replace(/\D/g, '') || '1';
  const parsed = parsePhoneNumberFromString(phone || '', {
    defaultCallingCode,
  });
  if (parsed?.number) return parsed.number;
  if (phone) return formatPhoneForApi(phone.replace(/\D/g, '')) || '';
  return '';
};

/**
 * Converts to renterHistoryPayload
 */
const transformRenterHistoryPayload = (item: RentalHistoryFormObject[]) => {
  return item.map((item) => {
    const defaultCallingCode =
      (item?.landlordPhoneDetails?.countryCode || '').replace(/\D/g, '') || '1';
    const parsed = parsePhoneNumberFromString(item?.landlordPhoneNumber || '', {
      defaultCallingCode,
    });
    return {
      type: item?.type || 'RENTAL',
      landlord_name: item?.landlordName || '',
      landlord_email: item?.landlordEmail || '',
      landlord_phone_number: parsed?.number || '',
      start_date: item?.startDate
        ? formatDate(item.startDate, DATE_FORMATS.BACKEND_DATE_FORMAT)
        : '',
      end_date: item?.endDate
        ? formatDate(item.endDate, DATE_FORMATS.BACKEND_DATE_FORMAT)
        : undefined,
      current_address: item?.currentAddress || '',
      address_place_id: item?.addressPlaceId || '',
      still_living_here: item?.stillLivingHere || false,
      mortgage: item?.mortgage
        ? String(item.mortgage).replace(/[^0-9]/g, '')
        : 0,
      rent_payment: item?.rentPayment
        ? String(item.rentPayment).replace(/[^0-9]/g, '')
        : 0,
      unit_code: item?.unitCode || '',
    };
  });
};

/**
 * This method is used to convert API response to FE response which we used in other component.
 * The reason behind creating this helper function is If api missing key then FE will not break.
 *
 * @param {Array} profile
 * @returns {Array} Custom response
 */
const transformRenterProfile = (
  profile: RenterApiProfile | undefined | null
): RenterProfile | null => {
  if (!profile) return null;

  return {
    id: profile?.id,
    relatedAgent: profile?.related_agent || '',
    agentInformation: {
      bio: profile?.agent_information?.bio || '',
      companyName: profile?.agent_information?.company_name || '',
      profilePicture: profile?.agent_information?.profile_picture || '',
      profilePictureThumbnail:
        profile?.agent_information?.profile_picture_thumbnail || '',
      companyPrimaryAddress:
        profile?.agent_information?.company_primary_address || '',
      companyAddressPlaceId:
        profile?.agent_information?.company_address_placeid || '',
      companyAddressLineTwo:
        profile?.agent_information?.company_address_line_two || '',
      licenseNumber: profile?.agent_information?.license_number || '',
      enableOneoffCredit:
        profile?.agent_information?.enable_oneoff_credit || false,
      enableGeneralApplications:
        profile?.agent_information?.enable_general_applications || false,
      creditOneoffUrlKey:
        profile?.agent_information?.credit_oneoff_url_key || '',
      aiEnabled: profile?.agent_information?.ai_enabled || false,
      manualApproved: profile?.agent_information?.manually_approved || false,
      profileUrlIdentifier:
        profile?.agent_information?.profile_url_identifier || '',
      nylasGrant: profile?.agent_information?.nylas_grant || false,
      companyLogo: profile?.agent_information?.company_logo || null,
    },
    landlordInformation: {
      bio: profile?.landlord_information?.bio || '',
      companyName: profile?.landlord_information?.company_name || '',
      companyPrimaryAddress:
        profile?.landlord_information?.company_primary_address || '',
      companyAddressPlaceId:
        profile?.landlord_information?.company_address_placeid || '',
      companyAddressLineTwo:
        profile?.landlord_information?.company_address_line_two || '',
      profilePicture: profile?.landlord_information?.profile_picture || '',
      profilePictureThumbnail:
        profile?.landlord_information?.profile_picture_thumbnail || '',
      manualApproved: profile?.landlord_information?.manually_approved || false,
      relatedAgent: profile?.related_agent || '',
      profileUrlIdentifier:
        profile?.landlord_information?.profile_url_identifier || '',
      nylasGrant: profile?.landlord_information?.nylas_grant || false,
    },
    generalInformation: {
      firstName: profile?.general_information?.first_name || '',
      middleName: profile?.general_information?.middle_name || '',
      lastName: profile?.general_information?.last_name || '',
      phoneNumber: profile?.general_information?.phone_number,
      phoneDetails: (() => {
        const pd = profile?.general_information?.phone_details;
        if (!pd) return undefined;
        return {
          e164: pd.e164 || '',
          countryCode: pd.country_code || '',
          nationalNumber: pd.national_number || '',
          regionCode: pd.region_code || '',
          formattedInternational: pd.formatted_international || '',
          formattedNational: pd.formatted_national || '',
          isMobile: pd.is_mobile ?? false,
        };
      })(),
      verified: profile?.general_information?.verified,
      email: profile?.general_information?.email,
      password: profile?.general_information?.password,
      notificationsTokenIos:
        profile?.general_information?.notifications_token_ios,
      notificationsTokenAndroid:
        profile?.general_information?.notifications_token_android,
      notificationsTokenWeb:
        profile?.general_information?.notifications_token_web,
      verifiedSms: profile?.general_information?.verified_sms || false,
      verifiedEmail: profile?.general_information?.verified_email || false,
      hasActiveSubscription:
        profile?.general_information?.has_active_subscription,
      currentSubscription: profile?.general_information?.current_subscription,
      paymentInstrumentDetails:
        profile?.general_information?.payment_instrument_details,
      isEmailNotificationsEnabled:
        profile?.general_information?.is_email_notifications_enabled,
    },
    renterInformation: {
      financialInformation: {
        creditInformation: {
          reportedCredit:
            profile?.renter_information?.financial_information
              ?.credit_information?.reported_credit,
          ficoScore:
            profile?.renter_information?.financial_information
              ?.credit_information?.fico_score,
          transunionScreeningId:
            profile?.renter_information?.financial_information
              ?.credit_information?.transunion_screening_id,
          reportFile:
            profile?.renter_information?.financial_information
              ?.credit_information?.report_file,
          expiryDate:
            profile?.renter_information?.financial_information
              ?.credit_information?.expiry_date,
          creditCheckStatus:
            profile?.renter_information?.financial_information
              ?.credit_information?.credit_check_status,
          lastPulledCreditTime:
            profile?.renter_information?.financial_information
              ?.credit_information?.last_pulled_credit_time,
          transunionStatus:
            profile?.renter_information?.financial_information
              ?.credit_information?.transunion_status,
          paymentTransferId:
            profile?.renter_information?.financial_information
              ?.credit_information?.payment_transfer_id,
          dateOfBirth:
            profile?.renter_information?.financial_information
              ?.credit_information?.date_of_birth,
          totalPullsInSession:
            profile?.renter_information?.financial_information
              ?.credit_information?.total_pulls_in_session,
        },
        salaryInformation: {
          reportedSalary:
            profile?.renter_information?.financial_information
              ?.salary_information?.reported_salary,
          determinedIncome:
            profile?.renter_information?.financial_information
              ?.salary_information?.determined_income,
          plaidVerificationPublicToken:
            profile?.renter_information?.financial_information
              ?.salary_information?.plaid_verification_public_token,
        },
        employmentInformation: {
          timeAtWork:
            profile?.renter_information?.financial_information
              ?.employment_information?.time_at_work,
          location:
            profile?.renter_information?.financial_information
              ?.employment_information?.location,
          lengthOfCommute:
            profile?.renter_information?.financial_information
              ?.employment_information?.length_of_commute,
          commuteMethod:
            profile?.renter_information?.financial_information
              ?.employment_information?.commute_method,
          employerAddressPlaceId:
            profile?.renter_information?.financial_information
              ?.employment_information?.employer_address_place_id,
          employerEmail:
            profile?.renter_information?.financial_information
              ?.employment_information?.employer_email,
          employerName:
            profile?.renter_information?.financial_information
              ?.employment_information?.employer_name,
          employerPrimaryAddress:
            profile?.renter_information?.financial_information
              ?.employment_information?.employer_primary_address,
          employmentLengthInMonths:
            Number(
              profile?.renter_information?.financial_information
                ?.employment_information?.employment_length_in_months
            ) || 0,
          employmentLengthInYears:
            Number(
              profile?.renter_information?.financial_information
                ?.employment_information?.employment_length_in_years
            ) || 0,
          jobTitle:
            profile?.renter_information?.financial_information
              ?.employment_information?.job_title || '',
          managerName:
            profile?.renter_information?.financial_information
              ?.employment_information?.manager_name || '',
          managerPhoneNumber:
            profile?.renter_information?.financial_information
              ?.employment_information?.manager_phone_number || '',
          managerPhoneDetails: {
            countryCode:
              profile?.renter_information?.financial_information
                ?.employment_information?.manager_phone_details?.country_code ||
              '+1',
            nationalNumber:
              profile?.renter_information?.financial_information
                ?.employment_information?.manager_phone_details
                ?.national_number || '',
          },
          isUnemployed:
            profile?.renter_information?.financial_information
              ?.employment_information?.is_unemployed || false,
          unemploymentReason:
            profile?.renter_information?.financial_information
              ?.employment_information?.unemployment_reason || '',
          voucherInformation: {
            voucherType:
              profile?.renter_information?.financial_information
                ?.employment_information?.voucher_information?.voucher_type ||
              '',
            issuingAgency:
              profile?.renter_information?.financial_information
                ?.employment_information?.voucher_information?.issuing_agency ||
              '',
            voucherAmount:
              profile?.renter_information?.financial_information
                ?.employment_information?.voucher_information?.voucher_amount ||
              '',
            caseWorkerName:
              profile?.renter_information?.financial_information
                ?.employment_information?.voucher_information
                ?.case_worker_name || '',
            caseWorkerNumber:
              profile?.renter_information?.financial_information
                ?.employment_information?.voucher_information
                ?.case_worker_number || '',
            caseWorkerPhoneDetails: {
              countryCode:
                profile?.renter_information?.financial_information
                  ?.employment_information?.voucher_information
                  ?.case_worker_phone_details?.country_code || '+1',
              nationalNumber:
                profile?.renter_information?.financial_information
                  ?.employment_information?.voucher_information
                  ?.case_worker_phone_details?.national_number || '',
            },
            caseWorkerEmail:
              profile?.renter_information?.financial_information
                ?.employment_information?.voucher_information
                ?.case_worker_email || '',
          },
        },
      },
      neighborhoodPreferenceInformation: {
        nearbyParks:
          profile?.renter_information?.neighborhood_preference_information
            ?.nearby_parks,
        schoolRankings:
          profile?.renter_information?.neighborhood_preference_information
            ?.school_rankings,
        pointsOfInterest:
          profile?.renter_information?.neighborhood_preference_information
            ?.points_of_interest,
        commuteTime:
          profile?.renter_information?.neighborhood_preference_information
            ?.commute_time,
      },
      amenityInformation: profile?.renter_information?.amenity_information,
      identityInformation: {
        photoId: profile?.renter_information?.identity_information?.photo_id,
        rentalHistory:
          profile?.renter_information?.identity_information?.rental_history,
      },
      rentalHistories: (
        profile?.renter_information?.rental_histories || []
      ).map((history) => {
        const phoneDetails = (
          history as { landlord_phone_details?: PhoneDetailsApi }
        ).landlord_phone_details;
        return {
          type: history?.type || 'RENTAL',
          landlordName: history?.landlord_name || '',
          landlordEmail: history?.landlord_email || '',
          landlordPhoneNumber: history?.landlord_phone_number || '',
          landlordPhoneDetails: {
            countryCode: phoneDetails?.country_code || '',
            nationalNumber: phoneDetails?.national_number || '',
          },
          startDate: history?.start_date || '',
          endDate: history?.end_date || '',
          currentAddress: history?.current_address || '',
          addressPlaceId: history?.address_place_id || '',
          stillLivingHere: history?.still_living_here || false,
          mortgage: +history?.mortgage > 0 ? history?.mortgage : '',
          rentPayment: +history?.rent_payment > 0 ? history?.rent_payment : '',
          unitCode: history?.unit_code || '',
        };
      }),
      documentInformation: (
        profile?.renter_information?.document_information || []
      ).map((document) => {
        return {
          ...document,
          id: String(document.id),
          uploadDate: document.upload_date,
        };
      }),
      kidsAndPets: {
        totalOccupants: Number(
          profile?.renter_information?.household_information === null ||
            profile?.renter_information?.household_information
              ?.total_occupants === null
            ? 1
            : profile?.renter_information?.household_information
                ?.total_occupants
        ),
        totalAdults: Number(
          profile?.renter_information?.household_information?.total_adults
        ),
        totalChildren: Number(
          profile?.renter_information?.household_information?.total_children
        ),
        moveInDate:
          profile?.renter_information?.household_information?.move_in_date ??
          null,
        smallDogs: Number(
          profile?.renter_information?.household_information?.small_dogs
        ),
        mediumDogs: Number(
          profile?.renter_information?.household_information?.medium_dogs
        ),
        largeDogs: Number(
          profile?.renter_information?.household_information?.large_dogs
        ),
        cats: Number(profile?.renter_information?.household_information?.cats),
        otherPets: Number(
          profile?.renter_information?.household_information?.other_pets
        ),
      },
    },
    creationDate: profile?.creation_date,
    lastUpdate: profile?.last_update,
  };
};

/**
 * This method is used to create the initial edit renter profile object
 * @param {RenterProfile | undefined | null} profile
 * @returns {InitialEditRenterValues} Custom response
 */
const transformEditRenterProfile = (
  profile: RenterProfile | undefined | null
) => {
  return {
    first_name: profile?.generalInformation?.firstName ?? '',
    middle_name: profile?.generalInformation?.middleName ?? '',
    last_name: profile?.generalInformation?.lastName ?? '',
    phone_number: profile?.generalInformation?.phoneNumber ?? '',
    email: profile?.generalInformation?.email ?? '',
    password: '',
    confirm_password: '',
    location:
      profile?.renterInformation?.financialInformation?.employmentInformation
        ?.location ?? '',
    time:
      getHHMMFromTime(
        String(
          profile?.renterInformation?.financialInformation
            ?.employmentInformation?.timeAtWork
        )
      ) ?? '08:00',
    timeAbb:
      getAMPMFromTime(
        String(
          profile?.renterInformation?.financialInformation
            ?.employmentInformation?.timeAtWork
        )
      ) ?? 'AM',
    commuteMethod:
      lowerCase(
        profile?.renterInformation?.financialInformation?.employmentInformation
          ?.commuteMethod
      ) ?? '',
    timeSecond: profile?.renterInformation?.financialInformation
      ?.employmentInformation?.lengthOfCommute
      ? String(
          profile?.renterInformation?.financialInformation
            ?.employmentInformation?.lengthOfCommute
        )
      : '15',
    reported_salary: String(
      !profile?.renterInformation?.financialInformation?.salaryInformation
        ?.reportedSalary
        ? 0
        : profile?.renterInformation?.financialInformation?.salaryInformation
            ?.reportedSalary
    ),
    reported_credit: String(
      !profile?.renterInformation?.financialInformation?.creditInformation
        ?.reportedCredit
        ? 0
        : profile?.renterInformation?.financialInformation?.creditInformation
            ?.reportedCredit
    ),
    amenities: profile?.renterInformation?.amenityInformation?.amenities?.length
      ? profile?.renterInformation?.amenityInformation?.amenities?.map(
          (amenity: Amenity) => amenity?.type
        )
      : [],
  } as InitialEditRenterValues;
};

/**
 * This method is used to create the initial edit renter profile object
 * @param {RenterProfile | undefined | null} profile
 * @returns {InitialEditRenterValues} Custom response
 */
const transformEditAgentProfile = (
  profile: RenterProfile | undefined | null
) => {
  return {
    first_name:
      profile?.generalInformation?.firstName ??
      profile?.generalInformation?.firstName ??
      '',
    middle_name: profile?.generalInformation?.middleName ?? '',
    last_name: profile?.generalInformation?.lastName ?? '',
    company_address:
      (profile?.agentInformation?.companyPrimaryAddress ||
        profile?.landlordInformation?.companyPrimaryAddress) ??
      '',
    company_address_place_id:
      (profile?.agentInformation?.companyAddressPlaceId ||
        profile?.landlordInformation?.companyAddressPlaceId) ??
      '',
    company_name:
      (profile?.agentInformation?.companyName ||
        profile?.landlordInformation?.companyName) ??
      '',
    company_address_line_two:
      (profile?.agentInformation?.companyAddressLineTwo ||
        profile?.landlordInformation?.companyAddressLineTwo) ??
      '',
    license_number: profile?.agentInformation?.licenseNumber ?? '',
    phone_number: profile?.generalInformation?.phoneNumber ?? '',
    email: profile?.generalInformation?.email ?? '',
    bio:
      (profile?.agentInformation?.bio || profile?.landlordInformation?.bio) ??
      '',
    password: '',
    confirm_password: '',
    terms: true,
  } as InitialAgentRegisterValues;
};

/**
 * This method is used to create payload for patch renter profile api
 * @param {InitialEditRenterValues} profile
 * @returns {InitialEditRenterValues} Custom response
 */
const transformStateToPayloadProfile = (profile: InitialEditRenterValues) => {
  return {
    general_information: {
      first_name: profile?.first_name || '',
      middle_name: profile?.middle_name || '',
      last_name: profile?.last_name || '',
      phone_number: profile?.phone_number || '',
    },
    renter_information: {
      financial_information: {
        credit_information: {
          reported_credit:
            profile?.reported_credit && Number(profile?.reported_credit) > 300
              ? Number(profile?.reported_credit)
              : null,
        },
        salary_information: {
          reported_salary:
            profile?.reported_salary && Number(profile?.reported_salary) > 0
              ? Number(profile?.reported_salary)
              : null,
        },
        employment_information: {
          time_at_work: profile.time ?? null,
          location: profile.location ?? null,
          length_of_commute: Number(profile.timeSecond) ?? null,
          commute_method: capitalize(profile.commuteMethod) ?? null,
        },
      },
      amenity_information: {
        amenities: profile?.amenities.length
          ? profile?.amenities.map((amenity: string) => ({
              type: amenity,
              included: true,
            }))
          : [],
      },
    },
  } as Partial<RegisterRenterPayload>;
};

/**
 * This method is used to create payload for patch agent profile api
 * @param {AgentRegisterEditValues} profile
 * @returns {InitialEditRenterValues} Custom response
 */
const transformStateToPayloadAgentProfile = (
  profile: AgentRegisterEditValues,
  isEdit: boolean
) => {
  const payload = isEdit
    ? ({
        general_information: {
          first_name: profile.first_name || '',
          middle_name: profile.middle_name || '',
          last_name: profile.last_name || '',
          // phone_number: profile.phone_number.replace(/[^0-9]/g, '') || '',
          verified: false,
          notifications_token: 'test',
        },
        agent_information: {
          bio: profile?.bio || '',
          profile_picture: profile.file.base64 || '',
          company_name: profile.company_name || '',
          license_number: profile.license_number || '',
          company_address_line_two: profile.company_address_line_two || '',
        },
      } as AgentRegisterPayload)
    : ({
        general_information: {
          first_name: profile.first_name || '',
          middle_name: profile.middle_name || '',
          last_name: profile.last_name || '',
          phone_number: profile.phone_number.replace(/[^0-9]/g, '') || '',
          verified: false,
          email: profile.email || '',
          password: profile.password || '',
          notifications_token: 'test',
        },
        agent_information: {
          license_number: profile.license_number || '',
          bio: profile?.bio || '',
          profile_picture: profile.file.base64 || '',
          company_address_placeid: profile.company_address_place_id || '',
          company_primary_address: profile.company_address || '',
          company_name: profile.company_name || '',
          company_address_line_two: profile.company_address_line_two || '',
        },
      } as AgentRegisterPayload);

  if (!profile.company_address_line_two && payload.agent_information) {
    delete payload.agent_information.company_address_line_two;
  }

  if (!profile?.file?.file && payload?.agent_information) {
    delete payload.agent_information.profile_picture;
  }

  if (!profile.license_number && payload.agent_information) {
    delete payload.agent_information.license_number;
  }

  return payload;
};

/**
 * This method is used to create payload for patch agent profile api
 * @param {AgentRegisterEditValues} profile
 * @returns {InitialEditRenterValues} Custom response
 */
const transformStateToPayloadLandlordProfile = (
  profile: AgentRegisterEditValues,
  isEdit: boolean
) => {
  const payload = {
    general_information: {
      first_name: profile.first_name || '',
      middle_name: profile.middle_name || '',
      last_name: profile.last_name || '',
      phone_number: profile.phone_number.replace(/[^0-9]/g, '') || '',
      verified: false,
      email: profile.email || '',
      password: profile.password || '',
      notifications_token: 'test',
    },
    landlord_information: {
      bio: profile?.bio || '',
      profile_picture: profile.file.base64 || '',
      company_address_placeid: profile.company_address_place_id || '',
      company_name: profile.company_name || '',
      company_address_line_two: profile.company_address_line_two || '',
      company_primary_address: profile.company_address || '',
    },
  } as AgentRegisterPayload;

  if (!profile.company_address_line_two && payload.landlord_information) {
    delete payload.landlord_information.company_address_line_two;
  }

  if (isEdit && payload.general_information.phone_number) {
    delete payload.general_information.phone_number;
  }

  if (isEdit && payload.general_information.email) {
    delete payload.general_information.email;
  }

  if (!profile?.file?.file && payload?.landlord_information) {
    delete payload.landlord_information.profile_picture;
  }

  if (isEdit && 'password' in payload.general_information) {
    delete payload.general_information.password;
  }

  return payload;
};

/**
 * Combine address field as string
 *
 * @param {Object} address
 * @returns {String}
 */
const combineAddress = (address: Partial<Address> | undefined) => {
  const { primary = '', state = '', postalCode = '' } = address || {};

  const addressArr: string[] = [];

  if (primary) addressArr.push(primary);
  if (state) addressArr.push(state);
  if (postalCode) addressArr.push(postalCode);

  return addressArr.join(', ');
};

/**
 * Combine address field as string
 *
 * @param {Object} address
 * @returns {String}
 */
const combineAddressForFinix = (
  address: FinixAddress | undefined,
  isOnlyLine1 = false,
  isIgnoreLine1 = false
) => {
  const { line1, line2, city, region, postal_code } = address || {};

  const addressArr: string[] = [];

  if (line1 && !isIgnoreLine1) addressArr.push(line1);

  if (isOnlyLine1) {
    return line1;
  }

  if (line2) addressArr.push(line2);
  if (city) addressArr.push(city);
  if (region) addressArr.push(region);
  if (postal_code) addressArr.push(postal_code);

  return addressArr.join(', ');
};

/**
 * Combine address field as string
 *
 * @param {Object} address
 * @returns {String}
 */
const splitAddress = (
  address: Partial<Address> | undefined,
  showRoute?: boolean
) => {
  const {
    neighborhood = '',
    primary = '',
    state = '',
    postalCode = '',
    unitCode = '',
    route = '',
  } = address || {};

  const addressArr: string[] = [];
  const stateCityArr: string[] = [];

  showRoute
    ? route && addressArr.push(route)
    : (primary && addressArr.push(primary),
      unitCode && addressArr.push(unitCode));

  if (neighborhood) stateCityArr.push(neighborhood);
  if (state) stateCityArr.push(state);
  if (postalCode) stateCityArr.push(postalCode);

  return { title: addressArr.join(' #'), subTitle: stateCityArr.join(', ') };
};

/**
 * Combine address field as string
 *
 * @param {Object} address
 * @returns {String}
 */
const splitAddressWithString = (
  address: string | undefined,
  unitCode: string | undefined
) => {
  const title: string[] = [];
  let subTitle = '';
  if (address) {
    const parts = address.split(',');
    const primaryAdd = parts[0];
    title.push(String(primaryAdd));
    subTitle = parts.slice(1).join(',').trim() ?? '';
  }

  if (unitCode) {
    title.push(String(unitCode));
  }

  return { title: title.join(' #'), subTitle: subTitle };
};

/**
 * Returns a link to the property based on the property ID.
 *
 * @return {string} the link to the property
 */
const createPropertyLink = (propertyId: string) => {
  const propertyLink = propertyId
    ? `${window?.location?.origin}/property/${propertyId}`
    : '';
  return propertyLink;
};

/**
 * Build url to passing filters
 */

const propertyURLFilterBuilder = (filters: PropertyFilter, url: string) => {
  // Convert the URL string to a URL object
  const urlObject = new URL(url, window.location.origin);
  const paramsToRemove = [
    'keyword',
    'amenities',
    'is_easy_apply',
    'bedrooms',
    'bathrooms',
    'estimated_cost',
    'cost_min',
    'cost_max',
    'square_footage_max',
    'square_footage_min',
    'upfront_cost',
    'estimated_cost',
    'no_fee',
    'sort_by',
  ];
  for (const param of paramsToRemove) {
    urlObject.searchParams.delete(param);
  }
  if (filters.keyword) {
    urlObject.searchParams.set('keyword', encodeURIComponent(filters.keyword));
  }
  if (filters.isEasyApply) {
    urlObject.searchParams.set('is_easy_apply', 'true');
  }
  if (filters.bedrooms && filters.bedrooms?.length > 0) {
    urlObject.searchParams.set('bedrooms', filters.bedrooms.join(','));
  }
  if (filters.bathrooms && filters.bathrooms?.length > 0) {
    urlObject.searchParams.set('bathrooms', filters.bathrooms.join(','));
  }
  if (filters.estimatedCost) {
    urlObject.searchParams.set(
      'estimated_cost',
      filters.estimatedCost.toString()
    );
  }
  if (filters.costMin && filters.costMin > 0) {
    urlObject.searchParams.set('cost_min', filters.costMin.toString());
  }
  if (filters.costMax && filters.costMax < 50000) {
    urlObject.searchParams.set('cost_max', filters.costMax.toString());
  }
  if (filters.squareFootageMin && filters.squareFootageMin > 0) {
    urlObject.searchParams.set(
      'square_footage_min',
      filters.squareFootageMin.toString()
    );
  }
  if (filters.squareFootageMax && filters.squareFootageMax < 5000) {
    urlObject.searchParams.set(
      'square_footage_max',
      filters.squareFootageMax.toString()
    );
  }

  if (filters.upfrontCost) {
    urlObject.searchParams.set('upfront_cost', filters.upfrontCost.toString());
  }
  if (filters.estimatedCost) {
    urlObject.searchParams.set(
      'estimated_cost',
      filters.estimatedCost.toString()
    );
  }
  if (filters.moveIn) {
    urlObject.searchParams.set('move_in', filters.moveIn);
  }
  if (filters.amenity?.length) {
    urlObject.searchParams.set('amenities', filters.amenity.toString());
  }
  if (filters.noFee) {
    urlObject.searchParams.set('no_fee', 'true');
  }
  if (filters.sortBy) {
    urlObject.searchParams.set('sort_by', filters.sortBy);
  }

  // Convert the URL object back to a string
  const queryString = urlObject.search.slice(1);

  return `?${queryString}`;
};

const crmURLFilterBuilder = (filters: ApplicationCRMFilter, url: string) => {
  // Convert the URL string to a URL object
  const urlObject = new URL(url, window.location.origin);
  const paramsToRemove = [
    'keyword',
    'tags',
    'no_tags',
    'tag_condition',
    'status',
    'bedrooms',
    'bathrooms',
    'cost_min',
    'cost_max',
  ];
  for (const param of paramsToRemove) {
    urlObject.searchParams.delete(param);
  }
  if (filters.keyword) {
    urlObject.searchParams.set('keyword', encodeURIComponent(filters.keyword));
  }

  if (filters.tags?.length) {
    urlObject.searchParams.set('tags', filters.tags.toString());
  }

  if (filters.no_tags) {
    urlObject.searchParams.set('no_tags', 'true');
  }

  if (filters.tags?.length && filters.tagCondition) {
    urlObject.searchParams.set('tag_condition', filters.tagCondition);
  }

  if (filters.status) {
    urlObject.searchParams.set('status', filters.status);
  }

  if (filters.bedrooms) {
    urlObject.searchParams.set('bedrooms', filters.bedrooms.toString());
  }
  if (filters.bathrooms) {
    urlObject.searchParams.set('bathrooms', filters.bathrooms.toString());
  }

  if (filters.costMax) {
    urlObject.searchParams.set('cost_min', filters.costMin.toString());
    urlObject.searchParams.set('cost_max', filters.costMax.toString());
  }

  // Convert the URL object back to a string
  const queryString = urlObject.search.slice(1);

  return `?${queryString}`;
};

/**
 * Parse URL parameters back to ApplicationCRMFilter object
 */
const parseURLToCRMFilter = (
  searchParams: URLSearchParams
): ApplicationFilters => {
  const tagsParam = searchParams.get('tags');
  const tags = tagsParam ? tagsParam.split(',').filter(Boolean) : [];
  const bedroomsParam = searchParams.get('bedrooms');
  const bathroomsParam = searchParams.get('bathrooms');

  return {
    status: searchParams.get('status') || '',
    tags: tags,
    no_tags: searchParams.get('no_tags') === 'true',
    tagCondition: (searchParams.get('tag_condition') as 'OR' | 'AND') || 'OR',
    bathrooms: bathroomsParam ? bathroomsParam.split(',').filter(Boolean) : [],
    bedrooms: bedroomsParam ? bedroomsParam.split(',').filter(Boolean) : [],
    priceMax: parseFloat(searchParams.get('cost_max') ?? '0') || 0,
    priceMin: parseFloat(searchParams.get('cost_min') ?? '0') || 0,
  };
};

/**
 * Parse URL parameters back to PropertyFilter object
 */
const parseURLToPropertyFilter = (
  searchParams: URLSearchParams
): PropertyFilter => {
  const amenitiesParam = searchParams.get('amenities');
  const amenities = amenitiesParam
    ? amenitiesParam.split(',').filter(Boolean)
    : [];

  const bedroomsParam = searchParams.get('bedrooms');
  const bedrooms = bedroomsParam
    ? bedroomsParam.split(',').filter(Boolean)
    : [];

  const bathroomsParam = searchParams.get('bathrooms');
  const bathrooms = bathroomsParam
    ? bathroomsParam.split(',').filter(Boolean)
    : [];

  const sortBy = searchParams.get('sort_by') || '';
  const sortLabel =
    SORT_FILTER_MAP[sortBy as keyof typeof SORT_FILTER_MAP] || '';

  return {
    bathrooms: bathrooms,
    bedrooms: bedrooms,
    costMax: parseFloat(searchParams.get('cost_max') ?? '50000') || 50000,
    costMin: parseFloat(searchParams.get('cost_min') ?? '0') || 0,
    isEasyApply: searchParams.get('is_easy_apply') === 'true',
    keyword: searchParams.get('keyword') || '',
    limit: parseInt(searchParams.get('limit') ?? '20') || 20,
    moveIn: searchParams.get('move_in') || '',
    offset: parseInt(searchParams.get('offset') ?? '0') || 0,
    amenity: amenities,
    estimatedCost: parseFloat(searchParams.get('estimated_cost') ?? '0') || 0,
    squareFootageMax:
      parseFloat(searchParams.get('square_footage_max') ?? '5000') || 5000,
    squareFootageMin:
      parseFloat(searchParams.get('square_footage_min') ?? '0') || 0,
    upfrontCost: parseFloat(searchParams.get('upfront_cost') ?? '0') || 0,
    noFee: searchParams.get('no_fee') === 'true',
    sortBy: sortBy,
    sortLabel: sortLabel,
  };
};

const mapBoundUrlBuilder = (
  searchParams: URLSearchParams,
  coords: number[]
) => {
  let updatedQueryString = '';

  // Iterate through the existing parameters and build the query string
  for (const key of searchParams.keys()) {
    const value = searchParams.get(key);
    if (key === 'bound_coordinates') {
      // If 'bound_coordinates' parameter exists, replace its value
      updatedQueryString += `${key}=${coords.toString()}&`;
    } else {
      // Otherwise, keep the existing parameter
      updatedQueryString += `${key}=${value}&`;
    }
  }

  // If 'bound_coordinates' doesn't exist, add it to the query string
  if (!searchParams.has('bound_coordinates')) {
    updatedQueryString += `bound_coordinates=${coords.toString()}&`;
  }

  // Remove the trailing '&' character
  updatedQueryString = `?${updatedQueryString.slice(0, -1)}`;

  return updatedQueryString;
};

const sortUrlBuilder = (searchParams: URLSearchParams, sortBy: string) => {
  let updatedQueryString = '';

  // Iterate through the existing parameters and build the query string
  for (const key of searchParams.keys()) {
    const value = searchParams.get(key);
    if (key === 'sort_by') {
      // If 'bound_coordinates' parameter exists, replace its value
      updatedQueryString += `${key}=${sortBy}&`;
    } else {
      // Otherwise, keep the existing parameter
      updatedQueryString += `${key}=${value}&`;
    }
  }

  // If 'bound_coordinates' doesn't exist, add it to the query string
  if (!searchParams.has('sort_by')) {
    updatedQueryString += `sort_by=${sortBy}&`;
  }

  // Remove the trailing '&' character
  updatedQueryString = `?${updatedQueryString.slice(0, -1)}`;
  return updatedQueryString;
};

function removeQueryParam(url: string, param: string) {
  // Create a regular expression pattern to match the query parameter
  const pattern = new RegExp(`[?&]${param}=[^&]*(&|$)`, 'g');

  // Replace the matched pattern with an empty string to remove the parameter
  const updatedUrl = url.replace(pattern, '');

  // Remove any trailing "?" or "&" characters if they exist
  return updatedUrl.startsWith('?') ? updatedUrl : `?${updatedUrl}`;
}

/**
 * Build url to passing filters
 */
const propertyURLSearchBuilder = (
  filters: AddressAutoSearch[],
  url: string
) => {
  const urlObject = new URL(url, window.location.origin);
  const paramsToRemove = [
    'neighborhood',
    'zip_code',
    'exact_address',
    'address_label',
    'bound_coordinates',
  ];
  for (const param of paramsToRemove) {
    urlObject.searchParams.delete(param);
  }

  const urlFilters = filters
    .map((filter) => {
      return filter.main_text;
    })
    .join('|');

  if (filters.length > 0 && filters[0].types !== 'exact_address')
    urlObject.searchParams.set(filters[0].types, urlFilters);

  if (filters.length > 0 && filters[0].types === 'exact_address') {
    urlObject.searchParams.set(filters[0].types, urlFilters);
    urlObject.searchParams.set('address_label', filters[0].address);
  }

  const queryString = urlObject.search.slice(1);
  return `?${queryString}`;
};

/**
 * convert url_filter query to filter object
 */
const convertQueryStringToObject = (queryString: string) => {
  const params = new URLSearchParams(queryString);

  const sortBy = params.get('sort_by') || '';
  const sortLabel =
    SORT_FILTER_MAP[sortBy as keyof typeof SORT_FILTER_MAP] || '';

  const bedroomsParam = params.get('bedrooms');
  const bedrooms = bedroomsParam
    ? bedroomsParam.split(',').filter(Boolean)
    : [];

  const bathroomsParam = params.get('bathrooms');
  const bathrooms = bathroomsParam
    ? bathroomsParam.split(',').filter(Boolean)
    : [];

  const filter: PropertyFilter = {
    bathrooms: bathrooms,
    bedrooms: bedrooms,
    costMax: parseFloat(params.get('cost_max') ?? '0') || 0,
    costMin: parseFloat(params.get('cost_min') ?? '0') || 0,
    isEasyApply: params.get('easyapply') === 'true',
    keyword: params.get('keyword') || '',
    limit: parseInt(params.get('limit') ?? '20') || 20,
    moveIn: params.get('move_in') || '',
    offset: parseInt(params.get('offset') ?? '0') || 0,
    amenity: JSON.parse(params.get('amenities') ?? '[]') || [],
    estimatedCost: 0,
    squareFootageMax: 0,
    squareFootageMin: 0,
    upfrontCost: 0,
    noFee: false,
    sortBy: sortBy,
    sortLabel: sortLabel,
  };

  return filter;
};

/**
 *  Add commas in number
 */
const formatNumberWithCommas = (number: number): string => {
  return number?.toLocaleString('en-US');
};

/**
 * Parses a date string as local time to avoid timezone offset issues.
 * When new Date("2025-02-01") is called, JS treats it as UTC midnight,
 * which shifts to the previous day in US timezones (e.g., Jan 31).
 * Appending T00:00:00 forces local-time interpretation.
 */
const parseLocalDate = (date: string | Date): Date => {
  if (date instanceof Date) return date;
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return new Date(`${date}T00:00:00`);
  }
  return new Date(date);
};

/**
 *  For format Dates
 */
const formatDate = (date: string, dateFormat: string): string => {
  if (!date) return '';

  return format(parseLocalDate(date), dateFormat);
};

/**
 * Formats the available date based on whether it's available now or in the future
 * If the date is <= current date, returns "Available Now"
 * Otherwise, returns the date in MM/DD/YYYY format
 */
const formatAvailableDate = (
  date: string | Date | null | undefined
): string => {
  if (!date) return '';

  const availableDate = parseLocalDate(date);
  const currentDate = new Date();

  // Reset time to midnight for accurate date comparison
  availableDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);

  // If available date is today or in the past, show "Available Now"
  if (availableDate <= currentDate) {
    return 'Available Now';
  }

  // Otherwise, show the formatted date
  return format(availableDate, DATE_FORMATS.MM_DD_YYYY);
};

/**
 *  For format Times
 */
const formatTime = (timeString: string): string => {
  const date = new Date(timeString);
  return format(date, TIME_FORMATS.H_MM_A);
};

const formatDateRelative = (date: string): string => {
  const dateObj = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Check if it's today or yesterday
  if (dateObj.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (dateObj.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    // Format as "4 Sept"
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString('en-US', { month: 'short' });
    return `${day} ${month}`;
  }
};

/**
 * For the Date with Time
 * @param {string} date
 **/
const formatDateAndTime = (date: string) => {
  const isDate = formatDateRelative(date);
  const isTime = formatTime(date);
  return `${isDate} at ${isTime} `;
};

/**
 * This method is used to generate chart config for Up fort cost in detail modal.
 * @param {Object} chargeInformation
 * @param {Number} updatedCost
 * @returns {Object}
 */
const getUpFrontCostChartData = (
  chargeInformation: chargeInformation,
  updatedCost: number | null
) => {
  const copyOfChargeInformation = cloneDeep(chargeInformation);

  delete copyOfChargeInformation['extraFee'];

  let firstMonthCost = Object.values(copyOfChargeInformation)
    .filter((n) => n)
    .reduce((accumulator, currentValue) => {
      return accumulator + currentValue;
    }, 0);

  chargeInformation?.extraFee?.forEach((fee) => {
    firstMonthCost += fee.amount;
  });

  const rent: number[] = Array.from({ length: 12 }, () => Number(updatedCost));

  const deposit: number[] = Array.from({ length: 12 }, (_, i) =>
    i == 0 ? Number(chargeInformation.securityDeposit) : 0
  );

  const brokerFee: number[] = Array.from({ length: 12 }, (_, i) =>
    i == 0 ? Number(chargeInformation.brokerFee) : 0
  );

  const appFee: number[] = Array.from({ length: 12 }, (_, i) =>
    i == 0 ? Number(chargeInformation.applicationFee) : 0
  );

  const totalDeposit = deposit.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0
  );

  const totalBrokerFee = brokerFee.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0
  );

  const totalAppFee = appFee.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0
  );

  let totalOneTimeFees = 0;
  let totalMonthlyFees = 0;

  chargeInformation?.extraFee
    ?.filter((fee) => fee.fee_type === FEE_TYPES.ONE_TIME)
    .map((fee) => {
      totalOneTimeFees += Number(fee.amount);
    });

  chargeInformation?.extraFee
    ?.filter((fee) => fee.fee_type === FEE_TYPES.RECURRING)
    .map((fee) => {
      totalMonthlyFees += Number(fee.amount);
    });

  const oneTimeFee: number[] = Array.from({ length: 12 }, (_, i) =>
    i == 0 ? totalOneTimeFees : 0
  );

  const monthlyFee: number[] = Array.from(
    { length: 12 },
    () => totalMonthlyFees
  );

  const config = {
    firstMonthCost: firstMonthCost + rent[0] || 0,
    totalMonthlyFees: totalMonthlyFees,
    chart: {
      height: '320px',
      type: 'column',
      style: {
        fontFamily: 'Poppins, sans-serif',
      },
    },
    legend: {
      layout: 'horizontal',
      verticalAlign: 'top',
      align: 'right',
      itemDistance: 9,
      padding: 0,
      itemMarginTop: 0,
      itemMarginBottom: 20,
      itemStyle: {
        lineHeight: '14px',
      },
      reversed: true,
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: Array.from({ length: 12 }, (_, i) => `Month ${i + 1} `),
      crosshair: true,
      style: {
        stroke: '#eee',
      },
    },
    yAxis: {
      labels: {
        format: '${text}', // The $ is literally a dollar unit
      },
      min: 0,
      title: {
        text: '',
      },
      stackLabels: {
        enabled: true,
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-explicit-any
        formatter: function (this: any) {
          return formatCurrency(this.total, true);
        },
      },
    },
    tooltip: {
      formatter: function (): string[] {
        const tooltipTextArray = [];

        // Push Header
        tooltipTextArray.push(
          `<span style="font-size:13px;font-weight:600;">${this.x}</span><table style="margin-top:5px;">`
        );

        // List
        if ((this.points as Highcharts.Point[]) && this.points.length > 0) {
          this.points.forEach(
            (point: { series: { color: string; name: string }; y: number }) => {
              const pointText = `<tr><td style="padding:0">${
                point.series.name
              }: </td><td style="padding:0;color:${
                point.series.color
              };">&nbsp;&nbsp;${formatCurrency(point.y)}</td></tr>`;

              if (point.y > 0) tooltipTextArray.push(pointText);
            }
          );
        }

        // Push Footer
        tooltipTextArray.push(`</table>`);
        return tooltipTextArray;
      },
      shared: true,
      useHTML: true,
      points: [],
      x: '',
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        pointPadding: 0.2,
        borderWidth: 0,
      },
      series: {
        pointWidth: 20,
      },
    },
    series: [
      {
        name: 'Rent',
        data: rent,
        color: '#006AF9',
      },
    ],
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
  };

  // Check if series is empty
  if (totalDeposit > 0)
    config.series.push({
      name: 'Deposit',
      data: deposit,
      color: '#09C0F9',
    });

  if (totalBrokerFee > 0)
    config.series.push({
      name: 'Broker Fee',
      data: brokerFee,
      color: '#72DFFF',
    });

  if (totalAppFee > 0)
    config.series.push({
      name: 'App. Fee',
      data: appFee,
      color: '#CCCCFF',
    });

  if (totalMonthlyFees > 0)
    config.series.push({
      name: 'Monthly Fee(s)',
      data: monthlyFee,
      color: '#FF21F6',
    });

  if (totalOneTimeFees > 0)
    config.series.push({
      name: 'One-Time Fee(s)',
      data: oneTimeFee,
      color: '#D70FF5',
    });

  config.series.reverse();

  return config;
};

/**
 * This method is used to validate time in 12 hour format.
 * @param {string} time
 * @returns {Boolean}
 */
const isValidTime = (time: string, meridiem: string) => {
  if (!time) return false;
  // Regex to check valid
  // time in 12-hour format

  // Uncomment this line when we have to check regex like 10:11 AM
  // const regex = new RegExp(/((1[0-2]|0?[1-9]):([0-5][0-9]) ?([AaPp][Mm]))/);
  const regex = new RegExp(/((1[0-1]|0?[0-9]):([0-5][0-9]))/);

  const [hours] = time.split(':');
  if (meridiem === 'AM' && Number(hours) > 11) return false;

  return regex.test(time);
};

/**
 * This method is used to convert File to BlobURL.
 * @param {File} files
 * @returns {string[]}
 */
const transformFilesToBlobURL = (files: File[]) => {
  const fileBlobURL: string[] = files.map((file) => {
    if (file !== null) {
      return URL.createObjectURL(file);
    } else return '';
  });
  return fileBlobURL;
};

const transformFileToBlobURL = (file: File) => {
  const fileBlobURL: string = URL.createObjectURL(file);
  return fileBlobURL;
};

// Utility to convert Blob to File
const blobToFile = (blob: Blob, fileName: string, mimeType?: string) => {
  return new File([blob], fileName, { type: mimeType || blob.type });
};

/**
 * This method is used to generate the zeme-id format.
 * @param {number} id
 * @returns {string}
 */
const formatZemeId = (id: string) => {
  return id ? `#ZM-${id}` : `#ZM-0`;
};

const getApplicationStateAsPerRole = (
  role: string,
  states: ApplicationState[],
  currentState: string,
  applicantStatus?: string,
  isIncludedLandlord?: boolean
) => {
  let activeIndex = 1;
  const newStates = [...states].map((state) => {
    return {
      ...state,
      isCompleted: false,
    };
  });

  // Update isCompleted and label for application state for agent
  if (role === ZEME_USERS_ROLE.AGENT || role === ZEME_USERS_ROLE.LANDLORD)
    for (let i = 0; i < newStates.length; i++) {
      newStates[i].isCompleted = true;
      activeIndex = i + 1;

      if (
        newStates[i].value === APPLICATION_STATES_ENUM.LEASE_SENT ||
        newStates[i].value === APPLICATION_STATES_ENUM.LEASE_UPLOADED
      ) {
        newStates[i].label = APPLICATION_STATES_LABELS_ENUM.LEASE_SENT;
      }

      if (
        newStates[i].value ===
          APPLICATION_STATES_ENUM.LEASE_SIGNED_APPLICANTS &&
        !isIncludedLandlord
      ) {
        newStates[i].label = APPLICATION_STATES_LABELS_ENUM.LEASE_SIGNED;
      }

      if (
        newStates[i].value ===
          APPLICATION_STATES_ENUM.LEASE_SIGNED_APPLICANTS &&
        isIncludedLandlord
      ) {
        newStates[i].label = APPLICATION_STATES_LABELS_ENUM.APPLICANTS_SIGNED;
      }

      if (
        newStates[i].value === APPLICATION_STATES_ENUM.LEASE_SIGNED_LANDLORD &&
        isIncludedLandlord
      ) {
        newStates[i].label = APPLICATION_STATES_LABELS_ENUM.LANDLORD_SIGNED;
      }

      if (
        newStates[i].value ===
          APPLICATION_STATES_ENUM.LEASE_SIGNED_APPLICANTS &&
        currentState === APPLICATION_STATES_ENUM.LEASE_SIGNED_APPLICANTS &&
        isIncludedLandlord
      ) {
        newStates[i + 1].label =
          APPLICATION_STATES_LABELS_ENUM.AWAITING_SIGNATURE_LANDLORD;
      }

      if (newStates[i].value === APPLICATION_STATES_ENUM.PAYMENTS_REQUESTED) {
        newStates[i].label = APPLICATION_STATES_LABELS_ENUM.PAYMENT_REQUESTED;
      }

      if (newStates[i].value === APPLICATION_STATES_ENUM.PAYMENTS_RECEIVED) {
        newStates[i].label = APPLICATION_STATES_LABELS_ENUM.PAYMENTS_RECEIVED;
      }

      if (
        newStates[i].value === APPLICATION_STATES_ENUM.APPLICATION_SUBMITTED &&
        currentState === APPLICATION_STATES_ENUM.LEASE_UPLOADED &&
        newStates.length !== i + 1 &&
        newStates[i + 1].value === APPLICATION_STATES_ENUM.LEASE_SENT
      ) {
        newStates[i + 1].label = APPLICATION_STATES_LABELS_ENUM.LEASE_SENT;
        newStates[i + 1].buttonText = 'Open Draft';

        newStates[i].isCompleted = true;
        break;
      }

      if (
        newStates[i].value === APPLICATION_STATES_ENUM.APPLICATION_SUBMITTED &&
        currentState === APPLICATION_STATES_ENUM.IN_PROGRESS &&
        newStates.length !== i + 1
      ) {
        newStates[i].isCompleted = false;
        activeIndex = activeIndex - 1;
        break;
      }

      if (i + 1 === newStates.length) {
        if (currentState === APPLICATION_STATES_ENUM.CANCELLED)
          newStates[i].label = APPLICATION_STATES_LABELS_ENUM.CANCELLED;

        if (currentState === APPLICATION_STATES_ENUM.ACCEPTED)
          newStates[i].label =
            APPLICATION_STATES_LABELS_ENUM.APPLICATION_ACCEPTED;

        if (currentState === APPLICATION_STATES_ENUM.DENIED)
          newStates[i].label = APPLICATION_STATES_LABELS_ENUM.DENIED;
      }

      if (newStates[i].value == currentState && newStates.length !== i + 1) {
        // Change active label if needed if current state is active

        if (
          newStates[i + 1].value ===
            APPLICATION_STATES_ENUM.LEASE_SIGNED_APPLICANTS &&
          !isIncludedLandlord
        )
          newStates[i + 1].label =
            APPLICATION_STATES_LABELS_ENUM.AWAITING_SIGNATURE;

        break;
      }
    }

  // Update isCompleted and label for application state for renter
  if (role === ZEME_USERS_ROLE.RENTER)
    for (let i = 0; i < newStates.length; i++) {
      newStates[i].isCompleted = true;
      activeIndex = i + 1;

      if (currentState === APPLICATION_STATES_ENUM.LEASE_UPLOADED)
        newStates[i].label =
          APPLICATION_STATES_LABELS_ENUM.APPLICATION_VERIFIED;

      if (newStates[i].value === APPLICATION_STATES_ENUM.LEASE_SENT) {
        newStates[i].label =
          APPLICATION_STATES_LABELS_ENUM.APPLICATION_VERIFIED;
      }

      if (
        (newStates[i].value === APPLICATION_STATES_ENUM.LEASE_SENT &&
          applicantStatus === APPLICATION_CO_APPLICANT_ENUM.SIGNED) ||
        (newStates[i].value === APPLICATION_STATES_ENUM.LEASE_SENT &&
          applicantStatus ===
            APPLICATION_CO_APPLICANT_ENUM.SIGNED_WITHOUT_WEBHOOK)
      ) {
        newStates[i + 1].label = APPLICATION_STATES_LABELS_ENUM.LEASE_SIGNED;
        newStates[i + 1].buttonText = '';
      }

      if (
        newStates[i].value === APPLICATION_STATES_ENUM.LEASE_SIGNED_APPLICANTS
      ) {
        newStates[i].label = APPLICATION_STATES_LABELS_ENUM.LEASE_SIGNED;
      }

      if (newStates[i].value === APPLICATION_STATES_ENUM.PAYMENTS_REQUESTED) {
        newStates[i].label = APPLICATION_STATES_LABELS_ENUM.LEASE_VERIFIED;
      }

      if (newStates[i].value === APPLICATION_STATES_ENUM.PAYMENTS_RECEIVED) {
        newStates[i].label = APPLICATION_STATES_LABELS_ENUM.PAYMENT_PAID;
      }

      if (
        newStates[i].value ===
          APPLICATION_STATES_ENUM.LEASE_SIGNED_APPLICANTS &&
        currentState === APPLICATION_STATES_ENUM.LEASE_SIGNED_LANDLORD &&
        newStates.length !== i + 1
      ) {
        break;
      }

      if (i + 1 === newStates.length) {
        if (currentState === APPLICATION_STATES_ENUM.CANCELLED)
          newStates[i].label = APPLICATION_STATES_LABELS_ENUM.CANCELLED;

        if (currentState === APPLICATION_STATES_ENUM.ACCEPTED)
          newStates[i].label = APPLICATION_STATES_LABELS_ENUM.MOVE_IN;

        if (currentState === APPLICATION_STATES_ENUM.DENIED)
          newStates[i].label = APPLICATION_STATES_LABELS_ENUM.DENIED;
      }

      if (
        newStates[i].value === APPLICATION_STATES_ENUM.APPLICATION_SUBMITTED &&
        currentState === APPLICATION_STATES_ENUM.LEASE_UPLOADED
      )
        break;

      if (newStates[i].value == currentState && newStates.length !== i + 1) {
        break;
      }
    }

  return { states: newStates, activeIndex };
};

/**
 *This is used to display application state label as per current application state
 * @param {String} state
 * @returns {String}
 */
const getStateLabelForRenter = (state: string, applicantStatus: string) => {
  if (state === APPLICATION_STATES_ENUM.UNAVAILABLE) {
    return APPLICATION_STATES_LABELS_ENUM.UNAVAILABLE;
  }

  if (
    (state === APPLICATION_STATES_ENUM.LEASE_SENT &&
      applicantStatus === APPLICATION_CO_APPLICANT_ENUM.SIGNED) ||
    (state === APPLICATION_STATES_ENUM.LEASE_SENT &&
      applicantStatus === APPLICATION_CO_APPLICANT_ENUM.SIGNED_WITHOUT_WEBHOOK)
  ) {
    return APPLICATION_STATES_LABELS_ENUM.LEASE_SIGNED;
  }

  if (applicantStatus === APPLICATION_CO_APPLICANT_ENUM.COMPLETED) {
    return APPLICATION_STATES_LABELS_ENUM.APPLICATION_SUBMITTED;
  }

  if (applicantStatus === APPLICATION_CO_APPLICANT_ENUM.INVITED) {
    return 'Invite Sent';
  }

  if (applicantStatus === APPLICATION_CO_APPLICANT_ENUM.REJECTED) {
    return APPLICATION_STATES_LABELS_ENUM.INVITE_REJECTED;
  }

  switch (state) {
    case APPLICATION_STATES_ENUM.CANCELLED:
      return APPLICATION_STATES_LABELS_ENUM.CANCELLED;
    case APPLICATION_STATES_ENUM.ACCEPTED:
      return APPLICATION_STATES_LABELS_ENUM.SUBMITTED_AND_FINALIZED;
    case APPLICATION_STATES_ENUM.DENIED:
      return APPLICATION_STATES_LABELS_ENUM.DENIED;
    case APPLICATION_STATES_ENUM.IN_PROGRESS:
      return APPLICATION_STATES_LABELS_ENUM.IN_PROGRESS;
    case APPLICATION_STATES_ENUM.APPLICATION_SUBMITTED:
      return APPLICATION_STATES_LABELS_ENUM.APPLICATION_SUBMITTED;
    case APPLICATION_STATES_ENUM.LEASE_SENT:
    case APPLICATION_STATES_ENUM.LEASE_UPLOADED:
      return APPLICATION_STATES_LABELS_ENUM.SIGN_LEASE;
    case APPLICATION_STATES_ENUM.LEASE_SIGNED_LANDLORD:
      return APPLICATION_STATES_LABELS_ENUM.LANDLORD_SIGNED;
    case APPLICATION_STATES_ENUM.LEASE_SIGNED_APPLICANTS:
      return APPLICATION_STATES_LABELS_ENUM.LEASE_SIGNED;
    default:
      return APPLICATION_STATES_LABELS_ENUM.APPLICATION_IN_PROGRESS;
  }
};

/**
 *This is used to display application state label as per current application state
 * @param {String} state
 * @returns {String}
 */
const getStateLabelForAgent = (state: string) => {
  switch (state) {
    case APPLICATION_STATES_ENUM.CANCELLED:
      return APPLICATION_STATES_LABELS_ENUM.CANCELLED;
    case APPLICATION_STATES_ENUM.ACCEPTED:
      return APPLICATION_STATES_LABELS_ENUM.APPLICATION_APPROVAL;
    case APPLICATION_STATES_ENUM.DENIED:
      return APPLICATION_STATES_LABELS_ENUM.DENIED;
    case APPLICATION_STATES_ENUM.IN_PROGRESS:
      return APPLICATION_STATES_LABELS_ENUM.APPLICATION_IN_PROGRESS;
    case APPLICATION_STATES_ENUM.APPLICATION_SUBMITTED:
      return APPLICATION_STATES_LABELS_ENUM.APPLICATION_SUBMITTED;
    case APPLICATION_STATES_ENUM.LEASE_SENT:
    case APPLICATION_STATES_ENUM.LEASE_UPLOADED:
      return APPLICATION_STATES_LABELS_ENUM.AWAITING_SIGNATURE;
    case APPLICATION_STATES_ENUM.LEASE_SIGNED_APPLICANTS:
      return APPLICATION_STATES_LABELS_ENUM.LEASE_SIGNED;
    default:
      return APPLICATION_STATES_LABELS_ENUM.APPLICATION_IN_PROGRESS;
  }
};

/**
 * This method is used for the sorting in the application listing page.
 * @param {String} type
 * @returns {Array}
 */
const getApplicationSorting = (
  applications: AllApplication[],
  type: SORT_OPTIONS
): AllApplication[] => {
  switch (type) {
    case SORT_OPTIONS.NEWEST_TO_OLDEST:
      return [...applications].sort(
        (a, b) =>
          new Date(b.creation_date).getTime() -
          new Date(a.creation_date).getTime()
      );
    case SORT_OPTIONS.OLDEST_TO_NEWEST:
      return [...applications].sort(
        (a, b) =>
          new Date(a.creation_date).getTime() -
          new Date(b.creation_date).getTime()
      );
    case SORT_OPTIONS.INCOME_LOW_TO_HIGH:
      return [...applications].sort((a: AllApplication, b: AllApplication) => {
        const aReportedCredit = a.applicant_information.applicants.reduce(
          (totalCredit: number, applicant: Applicant) =>
            totalCredit +
            applicant.renter.renter_information.financial_information
              .credit_information.reported_credit,
          0
        );
        const bReportedCredit = b.applicant_information.applicants.reduce(
          (totalCredit: number, applicant: Applicant) =>
            totalCredit +
            applicant.renter.renter_information.financial_information
              .credit_information.reported_credit,
          0
        );
        return aReportedCredit - bReportedCredit;
      });
    case SORT_OPTIONS.INCOME_HIGH_TO_LOW:
      return [...applications].sort((a: AllApplication, b: AllApplication) => {
        const bReportedCredit = b.applicant_information.applicants.reduce(
          (totalCredit: number, applicant: Applicant) =>
            totalCredit +
            applicant.renter.renter_information.financial_information
              .credit_information.reported_credit,
          0
        );
        const aReportedCredit = a.applicant_information.applicants.reduce(
          (totalCredit: number, applicant: Applicant) =>
            totalCredit +
            applicant.renter.renter_information.financial_information
              .credit_information.reported_credit,
          0
        );
        return bReportedCredit - aReportedCredit;
      });

    default:
      return [...applications];
  }
};
/**
 * This method is used for the sorting the label in the application listing page.
 * @param {String} type
 * @returns {String}
 */
const getSortOptionLable = (type: SORT_OPTIONS) => {
  switch (type) {
    case SORT_OPTIONS.NEWEST_TO_OLDEST:
      return GET_SORT_APPLICATION_LABLE_ENUM.NEWEST_TO_OLDEST;
    case SORT_OPTIONS.OLDEST_TO_NEWEST:
      return GET_SORT_APPLICATION_LABLE_ENUM.OLDEST_TO_NEWEST;
    case SORT_OPTIONS.INCOME_LOW_TO_HIGH:
      return GET_SORT_APPLICATION_LABLE_ENUM.INCOME_LOW_TO_HIGH;
    case SORT_OPTIONS.INCOME_HIGH_TO_LOW:
      return GET_SORT_APPLICATION_LABLE_ENUM.INCOME_HIGH_TO_LOW;
    default:
      return GET_SORT_APPLICATION_LABLE_ENUM.NEWEST_TO_OLDEST;
  }
};

/**
 * This method is used for get the origin
 * @returns {String}
 */
const getOrigin = () => {
  const origin: string = window?.location?.origin || '';
  return origin;
};
/**
 * This method is used for get the origin
 * @returns {String}
 */
const getURLQueryString = () => {
  const search: string = window?.location?.search || '';
  return search;
};

/** This method is used for the AM/PM from the time.
 * @param {String} type
 * @returns {String}
 */
const getAMPMFromTime = (timeString: string) => {
  const match = timeString.match(/\b(?:AM|PM)\b/i);
  return match ? match[0] : null;
};

/**
 * This method is used for the time in the HH:MM formate from the string time.
 * @param {String} type
 * @returns {String}
 */
function getHHMMFromTime(time: string) {
  const strippedTime = time.replace(/\s*[APap][Mm]\s*$/, '');
  return strippedTime;
}

function getNameInitials(firstName?: string, lastName?: string) {
  let initials = '';

  // Get first letter of first name
  if (firstName && firstName.trim().length > 0) {
    initials += firstName.trim()[0].toUpperCase();
  }

  // Get first letter of last name
  if (lastName && lastName.trim().length > 0) {
    initials += lastName.trim()[0].toUpperCase();
  }

  return initials;
}

/**
 * Calculates the height of the dialog based on the window height.
 *
 * @return {number} The calculated height of the dialog.
 */
const getDialogHeight = (options: {
  isAddPadding?: boolean;
  isIncludeHeader?: boolean;
  extraPaddingToRemove?: number;
}) => {
  const {
    isAddPadding = false,
    isIncludeHeader = true,
    extraPaddingToRemove = 0,
  } = options || {};
  return (
    window.innerHeight -
      (56 +
        (isIncludeHeader ? 81 : 0) +
        (isAddPadding ? 40 : 0) +
        extraPaddingToRemove) || 0
  );
};

/**
 * Retrieves the name of the renter.
 *
 * @return {string} The name of the renter, or an empty string if not found.
 */
const getCurrentApplicantNameFromApplication = (
  application: AllApplication,
  applicantId?: string
) => {
  const currentApplicant = application?.applicant_information?.applicants.find(
    (applicant) => applicant?.renter?.id === applicantId
  );

  return currentApplicant
    ? currentApplicant?.renter?.general_information?.first_name
    : '';
};

/**
 * Check if the `navigator` object and the `userAgent` property are available
 *
 * @return {boolean} whether the user is on a mobile device
 */
const isMobile = () => {
  // Check if the `navigator` object and the `userAgent` property are available
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    // Regular expression to match common mobile device user agents
    const mobileDeviceRegex =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    // Check if the user agent string matches the regex
    return mobileDeviceRegex.test(navigator.userAgent);
  }
  // Return false if `navigator` or `userAgent` is not available (e.g., SSR)
  return false;
};

// Common utility functions
const extractPhoneDigits = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

/**
 * Formats phone for API: keeps + (E.164). Use this when sending phone_number to API.
 * @param phone - Phone string (E.164 with + or digits-only)
 * @returns Phone with + prefix (never stripped)
 */
const formatPhoneForApi = (phone: string): string => {
  if (!phone || typeof phone !== 'string') return phone;
  return phone.startsWith('+') ? phone : `+${phone}`;
};

/**
 * Formats a phone number in international format for display.
 * US: "+1 (947) 984-9986"; other countries: libphonenumber international format (e.g. "+91 98765 43210").
 * Accepts E.164 or national number; optional countryCode (dial code like "+1") when phone is national.
 * Returns the formatted string or the original input if parsing fails.
 */
const formatPhoneNumber = (
  phone: string | undefined,
  countryCode?: string | undefined
): string => {
  if (!phone || typeof phone !== 'string') return phone ?? '';
  const defaultCallingCode = countryCode
    ? (countryCode || '').replace(/\D/g, '') || '1'
    : undefined;
  const parsed = defaultCallingCode
    ? parsePhoneNumberFromString(phone, { defaultCallingCode })
    : parsePhoneNumberFromString(phone);
  if (!parsed) return phone;
  if (parsed.countryCallingCode === '1') {
    const n = parsed.nationalNumber;
    if (n.length >= 10) {
      return `+1 (${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6, 10)}`;
    }
  }
  return parsed.formatInternational();
};

// Dial code (e.g. "1", "+1") → default ISO2 for validation/masking. Built at load.
const DIAL_CODE_TO_ISO2: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  try {
    getCountries().forEach((iso2) => {
      try {
        const code = getCountryCallingCode(iso2);
        if (!map[code]) map[code] = iso2;
      } catch {
        /* skip */
      }
    });
    map['1'] = 'US';
    map['7'] = 'RU';
    map['44'] = 'GB';
    map['91'] = 'IN';
  } catch {
    map['1'] = 'US';
    map['91'] = 'IN';
  }
  return map;
})();

/**
 * Returns ISO2 country code for a dial code (e.g. "+1" → "US"). Used for validation and US-only masking.
 */
function getIso2FromDialCode(dialCode: string): string {
  if (!dialCode || typeof dialCode !== 'string') return 'US';
  const digits = dialCode.replace(/\D/g, '');
  return DIAL_CODE_TO_ISO2[digits] ?? 'US';
}

/**
 * Normalizes API country_code to dial code (e.g. "US" → "+1", "+1" or "91" → "+1" / "+91").
 */
function getDialCodeFromCountryCode(apiValue: string): string {
  if (!apiValue || typeof apiValue !== 'string') return '+1';
  const raw = apiValue.trim();
  if (raw.length === 2 && /^[A-Za-z]{2}$/.test(raw)) {
    try {
      return `+${getCountryCallingCode(raw.toUpperCase() as CountryCode)}`;
    } catch {
      return '+1';
    }
  }
  const digits = raw.replace(/\D/g, '');
  return digits ? `+${digits}` : '+1';
}

/**
 * Returns input mask for phone number by country (ISO2). US only; others return undefined.
 * Use for TextField mask prop so US shows (999) 999-9999 style.
 */
function getPhoneMask(iso2: string): string | undefined {
  if (!iso2 || iso2 !== 'US') return undefined;
  try {
    const example = getExampleNumber(iso2 as CountryCode, examples);
    const national = example?.formatNational?.() ?? '';
    const mask = national ? national.replace(/\d/g, '9') : '';
    return mask || '(999) 999-9999';
  } catch {
    return '(999) 999-9999';
  }
}

/**
 * Reformat phone display when country changes: keep digits, apply new format.
 * US: (XXX) XXX-XXXX; other countries: digits only.
 */
function formatPhoneDisplayForCountry(value: string, dialCode: string): string {
  const digits = (value || '').replace(/\D/g, '');
  if (!digits) return '';
  const iso2 = getIso2FromDialCode(dialCode);
  if (iso2 !== 'US') return digits;
  const d = digits.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
/**
 * Validates if a phone number is in the correct format
 * Accepts various formats and validates for 10-digit US phone numbers
 *
 * @param {string} phone - The phone number string to validate
 * @return {boolean} Returns true if the phone number is valid, false otherwise
 */
const isValidPhone = (phone: string): boolean => {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  // Remove all non-numeric characters
  const cleanPhone = extractPhoneDigits(phone);

  // Check if it's a valid 10-digit US phone number
  // Must be exactly 10 digits and not start with 0 or 1
  const phoneRegex = /^[2-9]\d{2}[2-9]\d{6}$/;

  return phoneRegex.test(cleanPhone);
};

/**
 * Validates if a given string is a valid email address.
 *
 * @param {string} email - The email address to validate.
 * @return {boolean} Returns true if the email is valid, false otherwise.
 */
const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Email regex that requires a valid TLD (at least 2 characters)
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

  return emailRegex.test(email.trim());
};

/**
 * Checks if a given date is in the past.
 *
 * @param {string} date - The date to check in ISO string format.
 * @return {boolean} Returns true if the date is in the past, false otherwise.
 */
const isPastDate = (date: string) => {
  if (!date) return false;

  const expiryDate = new Date(date);

  const [year, month, day, hours, minutes, seconds] = [
    expiryDate.getFullYear(),
    expiryDate.getMonth(),
    expiryDate.getDate(),
    expiryDate.getHours(),
    expiryDate.getMinutes(),
    expiryDate.getSeconds(),
  ];
  const dateToCompare = new Date(year, month, day, hours, minutes, seconds);

  if (isPast(dateToCompare)) {
    return true;
  }

  return false;
};

/**
 * A function that calculates the height of the page header.
 *
 * @return {number} The height of the page header.
 */
const getPageHeaderHeight = () => {
  if (typeof document !== 'undefined') {
    const header = document?.getElementById('zeme-header');

    if (header) {
      return header.offsetHeight;
    }
  }

  return DEFAULT_HEADER_HEIGHT;
};

/**
 * Returns the minimum and maximum credit scores from the given application.
 *
 * @param {Applicant} applicants - The application object containing applicant information.
 * @return {{minScore: number, maxScore: number}} - An object with the minimum and maximum credit scores.
 */
const getMinMaxCreditScore = (applicants: Applicant[]) => {
  const creditScore: number[] = [];
  let hasPending = false;
  if (!applicants) return { minScore: 0, maxScore: 0 };
  applicants?.forEach((applicant) => {
    if (!applicant?.credit_bypassed) {
      const ficoScore =
        applicant?.renter?.renter_information?.financial_information
          ?.credit_information?.fico_score;

      if (!ficoScore) {
        hasPending = true;
      } else {
        creditScore.push(Number(ficoScore));
      }
    }
  });

  if (creditScore.length === 0) {
    return {
      minScore: hasPending ? 'N/A' : -1,
      maxScore: hasPending ? 'N/A' : -1,
    };
  }

  return {
    minScore: Math.min(...creditScore) || 0,
    maxScore: Math.max(...creditScore) || 0,
  };
};

/**
 * Validates if the input date string matches the MM/DD/YYYY format and is a valid date.
 *
 * @param {string} date - The date string to be validated.
 * @return {boolean} Returns true if the date string is in the correct format and is a valid date, false otherwise.
 */
function isValidDate(date: string) {
  const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
  if (!regex.test(date)) {
    return false;
  }

  const [month, day, year] = date.split('/').map(Number);

  const currentYear = new Date().getFullYear();
  if (year < 1800 || year > currentYear + 400) {
    return false;
  }

  const daysInMonth = new Date(year, month, 0).getDate();

  if (day <= 0 || day > daysInMonth) {
    return false;
  }

  const dateObj = new Date(year, month - 1, day);
  return (
    dateObj.getFullYear() === year &&
    dateObj.getMonth() === month - 1 &&
    dateObj.getDate() === day
  );
}

/**
 * Removes all non-numeric characters from the input, inserts slashes at specific positions, and limits the input length to 10 characters.
 *
 * @param {string} input - The input string to be processed.
 * @return {string} The processed input string with non-numeric characters removed, slashes inserted, and length limited to 10 characters.
 */
function maskDateInput(input: string) {
  // Remove all non-numeric characters
  input = input.replace(/\D/g, '');

  // Insert slashes at the correct positions
  if (input.length > 2) {
    input = input.slice(0, 2) + '/' + input.slice(2);
  }
  if (input.length > 5) {
    input = input.slice(0, 5) + '/' + input.slice(5);
  }

  // Limit the input length to 10 characters
  if (input.length > 10) {
    input = input.slice(0, 10);
  }

  return input;
}

/**
 * Formats a number by returning an integer if the input number is an integer, otherwise returns the number with two decimal places.
 *
 * @param {number} number - The number to be formatted.
 * @return {number} The formatted number.
 */
function formatNumber(number: number) {
  return Number.isInteger(number) ? number : number.toFixed(2);
}

/**
 * Returns a valid name if the input name meets the specified criteria, otherwise returns an empty string.
 *
 * @param {string} name - The input name to be validated.
 * @return {string} The valid name if the input name is valid, otherwise an empty string.
 */
const getValidName = (name: string) => {
  return name &&
    name.length >= 2 &&
    name.length <= 50 &&
    NAME_PATTERN.test(name)
    ? name
    : '';
};

/**
 * Calculate page to offset
 * @param pageNumber Number
 * @param pageSize Number
 * @returns Number
 */
function pageToOffset(pageNumber: number, pageSize: number) {
  return (pageNumber - 1) * pageSize;
}

/**
 * Checks if the given state is New York
 *
 * @param {string} state - The state to check
 * @returns {boolean} true if the state is New York, false otherwise
 */
const findIsNyState = (state: string) => {
  return ['NY', 'ny', 'New York'].includes(state);
};

/**
 * Converts a hex color string to an rgba string
 *
 * @param {string} hex - The hex color string
 * @param {number} alpha - The alpha value
 * @return {string} The rgba string
 */
const hexToRGBA = (hex: string, alpha: number) => {
  let c = hex?.replace('#', '');
  if (c?.length === 3)
    c = c
      .split('')
      .map((x) => x + x)
      .join('');
  const num = parseInt(c, 16);
  return `rgba(${(num >> 16) & 255},${(num >> 8) & 255},${num & 255},${alpha})`;
};

// Utility to convert property payload to FormData
const propertyPayloadToFormData = (
  payload: CreatePropertyPayload
): FormData => {
  const formData = new FormData();

  // Stringify top-level objects
  if (payload.general_information) {
    formData.append(
      'general_information',
      JSON.stringify(payload.general_information)
    );
  }
  if (payload.economic_information) {
    formData.append(
      'economic_information',
      JSON.stringify(payload.economic_information)
    );
  }
  if (payload.amenity_information) {
    formData.append(
      'amenity_information',
      JSON.stringify(payload.amenity_information)
    );
  }
  if (payload.document_information) {
    formData.append(
      'document_information',
      JSON.stringify(payload.document_information)
    );
  }
  if (payload.charge_information) {
    formData.append(
      'charge_information',
      JSON.stringify(payload.charge_information)
    );
  }
  if (payload.utility_information) {
    formData.append(
      'utility_information',
      JSON.stringify(payload.utility_information)
    );
  }
  if (payload.statistic_information) {
    formData.append(
      'statistic_information',
      JSON.stringify(payload.statistic_information)
    );
  }
  if (payload.related_place_information) {
    formData.append(
      'related_place_information',
      JSON.stringify(payload.related_place_information)
    );
  }

  // Images (if you have files, otherwise just use URLs)
  if (
    payload.image_information &&
    Array.isArray(payload.image_information.images)
  ) {
    payload.image_information.images.forEach((img, idx: number) => {
      let file: File | undefined;
      if (img.image instanceof File) {
        file = img.image;
      }
      if (file) formData.append(`image[${idx}]`, file);
      // sequence_number is not a property, so use idx + 1 directly if needed by backend
      formData.append(`sequence_number[${idx}]`, String(idx + 1));
      if (img.rotation !== undefined) {
        formData.append(`rotation_degree[${idx}]`, String(img.rotation));
      }
    });
  }

  // Add any other fields as needed

  return formData;
};

const propertyUpdatePayloadToFormData = (
  payload: Partial<CreatePropertyPayload>
) => {
  const formData = new FormData();

  // Stringify all top-level objects if present
  if (payload.general_information) {
    formData.append(
      'general_information',
      JSON.stringify(payload.general_information)
    );
  }
  if (payload.economic_information) {
    formData.append(
      'economic_information',
      JSON.stringify(payload.economic_information)
    );
  }
  if (payload.amenity_information) {
    formData.append(
      'amenity_information',
      JSON.stringify(payload.amenity_information)
    );
  }
  if (payload.charge_information) {
    formData.append(
      'charge_information',
      JSON.stringify(payload.charge_information)
    );
  }
  if (payload.utility_information) {
    formData.append(
      'utility_information',
      JSON.stringify(payload.utility_information)
    );
  }
  if (payload.document_information) {
    formData.append(
      'document_information',
      JSON.stringify(payload.document_information)
    );
  }
  if (payload.statistic_information) {
    formData.append(
      'statistic_information',
      JSON.stringify(payload.statistic_information)
    );
  }
  if (payload.related_place_information) {
    formData.append(
      'related_place_information',
      JSON.stringify(payload.related_place_information)
    );
  }

  // Handle delete_image_ids as indexed fields if present
  if (payload.delete_image_ids && payload?.delete_image_ids?.length > 0) {
    formData.append(
      `delete_image_ids`,
      JSON.stringify(payload.delete_image_ids)
    );
  }

  // Images
  if (payload?.image_information) {
    payload.image_information?.images?.forEach(
      ({ image = '', id = null, rotation = '' }, idx) => {
        if (typeof image === 'string') {
          formData.append(`image[${idx}]`, image);
        } else if (image instanceof File) {
          formData.append(`image[${idx}]`, image);
        }
        formData.append(`sequence_number[${idx}]`, `${idx + 1}`);

        if (rotation !== undefined) {
          formData.append(`rotation_degree[${idx}]`, String(rotation || 0));
        }
        if (id) {
          formData.append(`image_id[${idx}]`, String(id));
        }
      }
    );
  }

  return formData;
};

/**
 * Normalize HTML (or plain text) for comparison so that semantically equivalent
 * content is considered equal. Handles whitespace and common HTML entities
 * that often differ between API (BE) and post-save content (e.g. &nbsp;, random
 * spaces, different Unicode space characters).
 */
function normalizeHtmlForComparison(html: string): string {
  if (typeof html !== 'string') return '';
  let s = html;
  // Decode HTML entities first so &nbsp; etc. become actual characters
  const entities: Record<string, string> = {
    '&nbsp;': '\u0020',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
  };
  Object.entries(entities).forEach(([entity, char]) => {
    s = s.replace(new RegExp(entity, 'gi'), char);
  });
  s = s.replace(/&#(\d+);/g, (_, code) =>
    String.fromCharCode(parseInt(code, 10))
  );
  s = s.replace(/&#x([0-9a-fA-F]+);/g, (_, code) =>
    String.fromCharCode(parseInt(code, 16))
  );
  // Strip spaces right after opening tags and right before closing tags so "<p> Upload" and "<p>Upload" compare equal
  s = s.replace(/>[\s\u00A0\u2000-\u200B\u202F\u205F\u3000]+/g, '>');
  s = s.replace(/[\s\u00A0\u2000-\u200B\u202F\u205F\u3000]+</g, '<');
  // Collapse remaining runs of space-like characters to a single space
  s = s.replace(/[\s\u00A0\u2000-\u200B\u202F\u205F\u3000]+/g, ' ');
  return s.trim();
}

/**
 * Normalize documents for comparison (e.g. change detection).
 * Includes name, category, template_id, and instruction fields so edits trigger dirty state.
 * Instruction text (HTML) is normalized so equivalent content compares equal.
 */
function normalizeDocumentsForComparison(docs: Documents[] | undefined): Array<{
  category: string;
  name: string;
  icon: string;
  template_id: string;
  instruction_text: string;
  instruction_scope: string;
}> {
  if (!docs || !Array.isArray(docs)) return [];
  return docs
    .map((d) => ({
      category: d.category || '',
      name: d.name || '',
      icon: d.icon || '',
      template_id: d.template_id || d.template || '',
      instruction_text: normalizeHtmlForComparison(
        d.instruction?.instruction_text ?? ''
      ),
      instruction_scope: (d.instruction?.instruction_scope ?? '')
        .trim()
        .toLowerCase(),
    }))
    .sort((a, b) =>
      a.category !== b.category
        ? a.category.localeCompare(b.category)
        : a.name.localeCompare(b.name)
    );
}

/**
 * Normalize collaborators for comparison (e.g. change detection).
 * Excludes markedForRemoval; compares email and role only.
 */
function normalizeCollaboratorsForComparison(
  collabInfo: CollaboratorInformation | undefined
): Array<{ email: string; role: string }> {
  if (!collabInfo?.collaborators || !Array.isArray(collabInfo.collaborators))
    return [];
  return collabInfo.collaborators
    .filter((c) => !c.markedForRemoval)
    .map((c) => ({ email: c.email || '', role: c.role || '' }))
    .sort((a, b) => a.email.localeCompare(b.email));
}

/**
 * Options for general-information comparison (e.g. exclude description when it is compared separately).
 */
export interface BuildGeneralFormValuesOptions {
  /** When true, omit description from the comparable object. Use on Descriptions step where description is tracked locally and payload updates only on Next/Back. */
  excludeDescription?: boolean;
}

/**
 * Build comparable general_information for change detection (same shape as Property Details form values).
 * Normalizes date_available and property_name so payload vs baseline comparison is consistent.
 */
function buildGeneralFormValuesForComparison(
  info: GeneralInformation | undefined,
  options?: BuildGeneralFormValuesOptions
): Record<string, unknown> | null {
  if (!info) return null;
  const base = {
    address: {
      place_id: info.address?.place_id,
    },
    unit: info.unit ?? '',
    floor: info.floor ?? '',
    bedrooms: info.bedrooms ?? '',
    bathrooms: info.bathrooms ?? '',
    square_footage: info.square_footage ?? '',
    date_available:
      formatDate(info.date_available, DATE_FORMATS.BACKEND_DATE_FORMAT) ||
      info.date_available ||
      '',
    is_exclusive: Boolean(info.is_exclusive),
    is_application_request_enabled: Boolean(
      info.is_application_request_enabled
    ),
  };
  if (options?.excludeDescription) {
    return base;
  }
  return { ...base, description: info.description ?? '' };
}

/**
 * Build comparable economic_information for change detection (same shape as Property Details form values).
 */
function buildEconomicFormValuesForComparison(
  info: EconomicInformationStep | undefined
): Record<string, unknown> | null {
  if (!info) return null;
  return {
    cost: info.cost ?? '',
    lease_concession: Boolean(info.lease_concession),
    lease_months_free: info.lease_months_free ?? '',
    lease_term: info.lease_term ?? '',
    net_effective_rent: info.net_effective_rent || '',
  };
}

/**
 * Build comparable amenity_information for change detection (same shape as Property Details: amenities array of { type, included: true }).
 */
function buildAmenityFormValuesForComparison(
  amenityInfo: AddPropertyAmenityInformation | undefined
): { amenities: Array<{ type: string; included: boolean }> } | null {
  if (!amenityInfo?.amenities || !Array.isArray(amenityInfo.amenities))
    return null;
  const amenities = amenityInfo.amenities
    .filter((a) => a.included)
    .map((a) => ({ type: a.type, included: true as const }))
    .sort((a, b) => a.type.localeCompare(b.type));
  return { amenities };
}

/** Comparable shape for one image (rotation, count, order; for files also name/size). */
export type NormalizedImageForComparison = {
  index: number;
  rotation: number;
  name?: string;
  size?: number;
};

/**
 * Build normalized image state for comparison (rotation, count, order, deleted).
 * Use for both payload (image_information.images) and local state (e.g. filesWithRotation in Photos).
 */
function buildNormalizedImagesForComparison(
  imagesArray: Array<{ image: unknown; rotation?: number }>,
  deleteImageIds?: string[]
): {
  images: NormalizedImageForComparison[];
  delete_image_ids: string[];
} {
  const images = (imagesArray || []).map((img, index) => {
    const file = img.image;
    if (file && typeof file === 'object' && 'name' in file) {
      const f = file as { name?: string; size?: number };
      return {
        index,
        rotation: img.rotation || 0,
        name: f.name ?? '',
        size: f.size ?? 0,
      };
    }
    return { index, rotation: img.rotation || 0 };
  });
  const delete_image_ids = [...(deleteImageIds || [])].sort();
  return { images, delete_image_ids };
}

/**
 * Normalize image_information and delete_image_ids for comparison (e.g. change detection for Photos step).
 * Covers: rotation, count change, image deleted, images order change.
 * Returns a single object so one isEqual() catches all image-related changes.
 */
function normalizeImagesForComparison(
  imageInfo: ImageInfoInterface | undefined,
  deleteImageIds?: string[]
): {
  images: NormalizedImageForComparison[];
  delete_image_ids: string[];
} {
  return buildNormalizedImagesForComparison(
    imageInfo?.images ?? [],
    deleteImageIds
  );
}
/**
 * Options to skip specific sections in makeComparable.
 * When a key is true, that section is omitted from the returned object.
 */
export type MakeComparableSkip = {
  general_information?: boolean;
  economic_information?: boolean;
  documents?: boolean;
  amenities?: boolean;
  collaborators?: boolean;
  images?: boolean;
};

function makeComparable(
  payload: AddPropertyPayload,
  options?: { skip?: MakeComparableSkip }
) {
  const skip = options?.skip ?? {};
  return {
    ...(!skip.general_information && {
      general_information: buildGeneralFormValuesForComparison(
        payload.general_information
      ),
    }),
    ...(!skip.economic_information && {
      economic_information: buildEconomicFormValuesForComparison(
        payload.economic_information
      ),
    }),
    ...(!skip.documents && {
      documents: normalizeDocumentsForComparison(
        payload.document_information?.documents
      ),
    }),
    ...(!skip.amenities && {
      amenities: buildAmenityFormValuesForComparison(
        payload.amenity_information
      ),
    }),
    ...(!skip.collaborators && {
      collaborators: normalizeCollaboratorsForComparison(
        payload.collaborator_information
      ),
    }),
    ...(!skip.images && {
      images: normalizeImagesForComparison(
        payload.image_information,
        payload.delete_image_ids
      ),
    }),
  };
}

/**
 * Format phone number with spaces around the dash for display purposes
 *
 * @param {string} phoneNumberString - the input phone number string
 * @return {string} the formatted phone number string with spaces around dash
 */
function formatPhoneNumberWithSpaces(phoneNumberString: string) {
  if (!phoneNumberString) return phoneNumberString;

  // Remove all non-numeric characters from the phone number string
  const cleaned = phoneNumberString.replace(/\D/g, '');

  // Format the cleaned phone number with spaces around dash
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]} - ${match[3]}`;
  }

  // If the phone number doesn't match the expected format, return the original string
  return phoneNumberString;
}

/**
 * Transforms One-Off Credit User Data from snake_case API response to camelCase
 * @param {OneOffCreditUserApiData} rawUserData - Raw API response data
 * @returns {OneOffCreditUserData} Transformed camelCase data
 */
const transformOneOffCreditUserData = (
  rawUserData: OneOffCreditUserApiData | undefined | null
): OneOffCreditUserData | null => {
  if (!rawUserData) return null;

  return {
    addressCity: rawUserData.address_city ?? '',
    addressPostalCode: rawUserData.address_postal_code ?? '',
    addressState: rawUserData.address_state ?? '',
    addressStreet: rawUserData.address_street ?? '',
    agent: rawUserData.agent ?? '',
    agentCanViewBackground: rawUserData.agent_can_view_background ?? false,
    applicationPropertyAddress: rawUserData.application_property_address ?? '',
    backgroundReport: rawUserData.background_report ?? '',
    creationDate: rawUserData.creation_date ?? '',
    creditCheckStatus: rawUserData.credit_check_status ?? '',
    creditReport: rawUserData.credit_report ?? '',
    dateOfBirth: rawUserData.date_of_birth ?? '',
    email: rawUserData.email ?? '',
    firstName: rawUserData.first_name ?? '',
    id: rawUserData.id ?? '',
    lastName: rawUserData.last_name ?? '',
    lastUpdate: rawUserData.last_update ?? '',
    middleName: rawUserData.middle_name ?? '',
    paymentTransferId: rawUserData.payment_transfer_id ?? '',
    phoneNumber: rawUserData.phone_number ?? '',
    status: rawUserData.status ?? '',
    totalPullsInSession: rawUserData.total_pulls_in_session ?? '',
  };
};

/**
 * Format time in a human-readable way (e.g., "1hr ago", "2d ago", "Just now")
 * @param timeString - ISO date string to format
 * @returns Formatted time string
 */
const formatTimeAgo = (timeString: string): string => {
  if (!timeString) return '';

  const date = new Date(timeString);
  const now = new Date();

  // For very recent messages (less than 1 minute)
  if (now.getTime() - date.getTime() < 60000) {
    return 'Just now';
  }

  // For messages from today, show relative time (e.g., "1hr ago", "5min ago")
  if (isToday(date)) {
    // For messages less than 1 hour old
    const minutesAgo = Math.floor((now.getTime() - date.getTime()) / 60000);
    if (minutesAgo < 60) {
      return `${minutesAgo}min ago`;
    }

    // For messages from today but more than 1 hour old
    const hoursAgo = Math.floor(minutesAgo / 60);
    return `${hoursAgo}hr ago`;
  }

  // For messages from yesterday
  if (isYesterday(date)) {
    return 'Yesterday';
  }

  // For messages within the last week
  const daysAgo = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (daysAgo < 7) {
    return `${daysAgo}d ago`;
  }

  // For older messages, show the date
  return format(date, 'MMM d');
};

/*
 * Get agent's full name
 * @param {Agent} agent - Agent data
 * @returns {string} The full name of the agent
 */
const getAgentFullName = (agent: Agent) => {
  return `${agent.generalInformation.firstName} ${agent.generalInformation.lastName}`.trim();
};

/**
 * Check if the given user type is an agent or landlord.
 * This is a common check used across the application for permission/routing logic.
 *
 * @param {string | undefined | null} userType - The user type to check
 * @returns {boolean} True if the user is an agent or landlord
 */
const isAgentOrLandlord = (userType?: string | null): boolean => {
  return (
    userType === ZEME_USERS_ROLE.AGENT || userType === ZEME_USERS_ROLE.LANDLORD
  );
};

/**
 * Check if the given user type is a renter.
 *
 * @param {string | undefined | null} userType - The user type to check
 * @returns {boolean} True if the user is a renter
 */
const isRenter = (userType?: string | null): boolean => {
  return userType === ZEME_USERS_ROLE.RENTER;
};

/**
 * Opens a file in a new tab.
 *
 * @param {string} link - The file URL to open.
 * @return {void}
 */
const openInNewTab = (link: string) => {
  window.open(link, '_blank');
};

export const getModalHeight = (
  showOtpField?: boolean,
  children?: React.ReactNode
) => {
  if (showOtpField) return '!h-[406px]';
  if (children) return '!h-auto';
  return 'min-h-[300px]';
};
/* Creates an image from a URL string
 * @param url - The image URL
 * @returns Promise that resolves with the Image object
 */
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

/**
 * Converts degrees to radians
 * @param degreeValue - Angle in degrees
 * @returns Angle in radians
 */
const getRadianAngle = (degreeValue: number): number => {
  return (degreeValue * Math.PI) / 180;
};

/**
 * Returns the new bounding area of a rotated rectangle
 * @param width - Original width
 * @param height - Original height
 * @param rotation - Rotation angle in degrees
 * @returns Object with new width and height after rotation
 */
const rotateSize = (
  width: number,
  height: number,
  rotation: number
): { width: number; height: number } => {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

/**
 * Crops an image based on the provided crop area, rotation, and flip settings
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param imageSrc - Source image URL or data URL
 * @param pixelCrop - Crop area coordinates and dimensions
 * @param rotation - Rotation angle in degrees (default: 0)
 * @param flip - Flip settings (default: { horizontal: false, vertical: false })
 * @returns Promise that resolves with the cropped image as a Blob
 */
const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  rotation = 0,
  flip = { horizontal: false, vertical: false }
): Promise<Blob | null> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');

  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // As a blob
  return new Promise((resolve) => {
    croppedCanvas.toBlob((fileObj: Blob | null) => {
      resolve(fileObj as Blob);
    }, 'image/png');
  });
};

/**
 * Get the delete confirmation message for removing a co-applicant.
 * Determines if the application will be auto-submitted based on the remaining applicants' status.
 *
 * @param {ApplicantType} selectedApplicant - The applicant being removed
 * @return {string} The confirmation message to display
 */
const getDeleteCoApplicantMessage = (
  selectedApplicant: ApplicantType,
  applicants: ApplicantType[]
): string => {
  // Find all applicants that are in progress or invited (not completed/rejected)
  const inProgressApplicants = applicants.filter(
    (applicant) =>
      applicant.status === APPLICATION_CO_APPLICANT_ENUM.IN_PROGRESS ||
      applicant.status === APPLICATION_CO_APPLICANT_ENUM.INVITED
  );

  // Find the primary applicant
  const primaryApplicant = applicants.find(
    (applicant) => applicant.is_primary_applicant
  );

  // Check if the selected applicant is not completed or rejected
  const isSelectedNotCompleted =
    selectedApplicant.status !== APPLICATION_CO_APPLICANT_ENUM.COMPLETED &&
    selectedApplicant.status !== APPLICATION_CO_APPLICANT_ENUM.REJECTED;

  // Check if primary applicant is completed
  const isPrimaryCompleted =
    primaryApplicant &&
    primaryApplicant.status === APPLICATION_CO_APPLICANT_ENUM.COMPLETED;

  // If there's only 1 in-progress/invited applicant (the one being removed),
  // and the selected applicant is not completed/rejected,
  // and the primary applicant is completed,
  // then the application will be auto-submitted
  const willAutoSubmit =
    isSelectedNotCompleted &&
    inProgressApplicants.length === 1 &&
    isPrimaryCompleted;

  if (willAutoSubmit) {
    return `After you remove ${selectedApplicant.email}, your application will be submitted automatically. If needed, you can always re-invite them later.`;
  } else {
    return `You are removing ${selectedApplicant.email}. If needed, you can always re-invite them later.`;
  }
};

const truncateName = (name: string, maxLength = 16): string => {
  if (!name) return '';
  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
};

/**
 * Filter unique names from aliases array (case-insensitive)
 * Excludes names that match the main name or are duplicates within aliases
 */
const getUniqueAliasNames = <T extends { name: string }>(
  mainName: string,
  aliases: T[] | undefined
): T[] => {
  if (!aliases || aliases.length === 0) return [];

  return aliases.filter((alias, index, arr) => {
    if (alias.name.toLowerCase() === mainName.toLowerCase()) return false;
    return (
      arr.findIndex(
        (a) => a.name.toLowerCase() === alias.name.toLowerCase()
      ) === index
    );
  });
};

/**
 * Filter unique phone numbers from aliases array
 * Excludes phone numbers that match the main phone or are duplicates within aliases
 */
const getUniqueAliasPhones = <T extends { phone_number: string }>(
  mainPhone: string,
  aliases: T[] | undefined
): T[] => {
  if (!aliases || aliases.length === 0) return [];

  return aliases.filter((alias, index, arr) => {
    if (alias.phone_number === mainPhone) return false;
    return (
      arr.findIndex((a) => a.phone_number === alias.phone_number) === index
    );
  });
};

/**
 * Get total count of unique phone numbers (main + unique aliases)
 */
const getTotalUniquePhoneCount = <T extends { phone_number: string }>(
  mainPhone: string,
  aliases: T[] | undefined
): number => {
  const uniqueAliasPhones = getUniqueAliasPhones(mainPhone, aliases);
  return 1 + uniqueAliasPhones.length;
};

/**
 * Returns the icon name for a given card brand
 * @param brand - The card brand from the API (e.g., 'VISA', 'MASTERCARD', 'AMEX', 'DISCOVER')
 * @returns The corresponding icon name to use with the Icon component
 */
const getCardBrandIcon = (brand?: string): string => {
  if (!brand) return 'visa';
  const normalizedBrand = brand.toLowerCase().replace(/\s/g, '');
  switch (normalizedBrand) {
    case 'visa':
      return 'visa';
    case 'mastercard':
      return 'mastercard';
    case 'amex':
    case 'americanexpress':
      return 'amex';
    case 'discover':
      return 'discover';
    default:
      return 'visa';
  }
};

export {
  transformAllPublicPropertiesData,
  transformPropertyItem,
  transformCreatePropertyPayloadItem,
  transFromPropertyToAddPropertyConext,
  transformAllAgentPropertiesData,
  transformEditRenterProfile,
  transformEditAgentProfile,
  transformStateToPayloadProfile,
  transformStateToPayloadAgentProfile,
  formatMapComponentData,
  combineAddress,
  splitAddress,
  splitAddressWithString,
  formatCurrency,
  createPropertyLink,
  propertyURLFilterBuilder,
  crmURLFilterBuilder,
  parseURLToCRMFilter,
  parseURLToPropertyFilter,
  propertyURLSearchBuilder,
  formatNumberWithCommas,
  convertQueryStringToObject,
  getUpFrontCostChartData,
  isValidTime,
  parseLocalDate,
  formatDate,
  formatAvailableDate,
  transformRenterProfile,
  transformFilesToBlobURL,
  transformFileToBlobURL,
  blobToFile,
  formatDateRelative,
  transformRenterHistoryPayload,
  transformOneOffCreditUserData,
  formatZemeId,
  formatTime,
  formatDateAndTime,
  getApplicationStateAsPerRole,
  getStateLabelForRenter,
  getStateLabelForAgent,
  getSortOptionLable,
  getApplicationSorting,
  getOrigin,
  getURLQueryString,
  getAMPMFromTime,
  getHHMMFromTime,
  mapBoundUrlBuilder,
  removeQueryParam,
  getNameInitials,
  sortUrlBuilder,
  getDialogHeight,
  getCurrentApplicantNameFromApplication,
  formatCurrencyTwoDigitWithRound,
  combineAddressForFinix,
  isMobile,
  formatPhoneNumber,
  extractPhoneDigits,
  formatPhoneForApi,
  getIso2FromDialCode,
  getDialCodeFromCountryCode,
  getPhoneMask,
  formatPhoneDisplayForCountry,
  isValidPhone,
  isValidEmail,
  transformStateToPayloadLandlordProfile,
  isPastDate,
  getPageHeaderHeight,
  getMinMaxCreditScore,
  translatePropertyAddPropertyAddress,
  isValidDate,
  maskDateInput,
  formatNumber,
  getValidName,
  pageToOffset,
  findIsNyState,
  hexToRGBA,
  propertyUpdatePayloadToFormData,
  propertyPayloadToFormData,
  formatPhoneNumberWithSpaces,
  formatTimeAgo,
  getAgentFullName,
  isAgentOrLandlord,
  isRenter,
  openInNewTab,
  createImage,
  getRadianAngle,
  rotateSize,
  getCroppedImg,
  transformPayloadToPropertyDetails,
  getDeleteCoApplicantMessage,
  truncateName,
  normalizeHtmlForComparison,
  normalizeDocumentsForComparison,
  normalizeCollaboratorsForComparison,
  normalizeImagesForComparison,
  buildNormalizedImagesForComparison,
  buildGeneralFormValuesForComparison,
  buildEconomicFormValuesForComparison,
  buildAmenityFormValuesForComparison,
  makeComparable,
  getUniqueAliasNames,
  getUniqueAliasPhones,
  getTotalUniquePhoneCount,
  getCardBrandIcon,
};
